// **The Rounds-mode composition root** (rounds-slice implementation-review **F1**).
//
// The exact counterpart of `SessionController` for the other mounted surface. `SessionController`
// owns the live match `Connection` and derives the live route seams from it; a rounds deployment
// mounts no socket at all, so this root owns the sandbox **credential**, one `RoundsHttpClient`,
// and derives the rounds route seams from it — and, like its live sibling, no screen constructs a
// client of its own.
//
// What it owns, in the order a boot needs it:
//
//   1. **The credential.** Restore it from durable storage — and *only* restore it. This is the
//      whole game: the credential is the only way back into an existing membership, so losing it
//      silently costs the player their programs and their runs.
//
//      **The boot branch inverted with `home-screen` (**D-b** and **D-c**).** It used to mint on boot
//      and then redirect `/` → `/colony`, treating the entry route as dead. That mint cannot
//      succeed: `mint_handler` refuses `unauthorized` unless the caller presents a join code or an
//      issued credential, and the client had no join-code concept anywhere, so on a fresh server
//      every boot was refused with no surface on which to supply the missing code. The root now
//      **mounts Home, restores in the background, and publishes the result as Home's Resume
//      affordance** — a mint happens on exactly two occasions, a restore and an explicit join, and
//      the root never navigates on its own.
//   2. **One `RoundsHttpClient`**, scoped to that one sandbox and that one token.
//   3. **The editor's seed.** `GET /rounds/{sandbox}/colony` into the store's `colonyMeta` — the
//      roster arrives over the socket in live mode, and there is no socket here.
//   4. **The run seam** (`colonyRun`) and **the review seams** (`roundsReview`), the two things
//      F1 found unreachable from a browser.
//   5. **The Beekeeper channel** (`colonyController` + `colonyPromptCooldown`) — F1's sibling
//      finding **F3**. One `RoundsPromptChannel` over the same client: `prompt` dispatches to the
//      sandbox's prompt route, a long-poll watch pushes the generation's statuses into the same
//      store slice the live socket feeds, an accepted generation re-reads the colony so the new
//      program appears in the editor, and the prompt box's advisory cooldown counts down the
//      **server-reported remainder** instead of a tick difference that a sandbox never changes.
//
// What it deliberately does NOT do: navigate to the review screen when a run completes (**D7** —
// a player may be mid-edit during the exact seconds a run lands, so entry is always an explicit
// action), and honour the other four `ScriptingMutation`s. The rounds surface exposes a Beekeeper
// prompt route and no hand-edit/rollback/create-type/birth-type route, so those four answer
// `not-ready` — exactly what the inert default already answered, and the truthful answer until a
// successor change adds the routes.
//
// ## The mode fork is one prop (`sandbox-screen-consolidation`, task 6.4)
//
// This root now hands `AppRoutes` a `sandboxScreen` element instead of the four Colony seams
// (`colonyRun`, `colonyController`, `colonyPromptCooldown`, `colonyChat`). The substitution is
// scoped to `/colony`; **`roundsReview` stays wired**, because `/review` must remain mounted for
// the graded subject even though nothing on the sandbox path links to it any more
// (`client-rounds-review`, MODIFIED). No screen gains a runtime `mode` conditional and the live
// composition root is untouched — the presence of the element IS the fork.
//
// ## Where the asynchrony lives (design **T1**/**T2**)
//
// `SandboxScreen` is a host: it dispatches events at a coordinator and draws what comes back. The
// **coordinator** owns intent epochs, the activate+load transaction, the save-then-run path and
// unmount cancellation; this root owns the two things a reducer cannot own — the transport it
// talks to (`SandboxPort`, six methods over the one `RoundsHttpClient`) and the timers that make
// it tick. That split is why task 8.3 can assert the screen's source contains no timer and issues
// no request.
//
// There are three of those timers, all here and none anywhere else: the **run-progress poll**
// (`RUN_PROGRESS_POLL_MS`), the **detail-residency probe**, and the **playback loop**
// (`playhead-loop.ts`), which turns animation frames into the coordinator's `playhead-advanced`.
// The loop is also why the selected **speed step** is held in this file rather than in the screen —
// the step is a multiplier on the loop's rate, and the loop is here.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactElement,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../App';
import {
  RoundsHttpClient,
  TournamentReadClient,
  type BearerRenewal,
  type RoundsTournamentTransport,
} from '../../net/rounds-http';
import {
  joinRoundsSession,
  mintFailureMessage,
  mintRoundsSession,
  restoreRoundsSession,
  type RoundsSession,
} from '../../net/rounds-session';
import { readJoinParam, stripJoinParam } from './qr-join';
import type { IdentityState } from '../../net/auth-identity';
import { fetchLeaderboard } from '../../net/rounds-leaderboard';
import { Home } from '../../ui/screens/Home';
import { Leaderboard } from '../../ui/screens/Leaderboard';
import { AmbientBackground } from '../../ui/home/AmbientBackground';
import { ambientLoopBytes } from '../../ui/home/ambient-asset';
import {
  createDispatchingTraceReader,
  type ReviewRun,
  type SandboxTraceClient,
} from '../../net/rounds-review-source';
import type { ColonyRunController, RunCandidate } from '../../ui/colony/run-controller';
import type { ChatSeam } from '../../ui/colony/chat-seam';
import type { ReviewRenderer } from '../../ui/components/rounds/ReviewFrame';
import type { SoundControlSeams } from '../../ui/components/hud/SoundControl';
import {
  performanceClock,
  rafFrameScheduler,
  type Clock,
  type FrameScheduler,
} from '../../canvas/frame-scheduler';
import type { Scheduler } from '../../net/wire/connection';
import type { ColonyMeta } from '../../net/wire/messages';
import { useClientStore } from '../../state/store';
import {
  SandboxScreen,
  type SandboxPracticeFraming,
  type SandboxPromptLock,
  type SandboxReviewResult,
  type SandboxTypeView,
} from '../../ui/screens/SandboxScreen';
import { Standings } from '../../ui/screens/Standings';
import type { GradedReviewViewerState, ReviewSubject } from '../../ui/screens/RoundsReview';
import {
  createSandboxCoordinator,
  initialSandboxState,
  watchableTrace,
  type DocumentSnapshot,
  type SandboxCoordinator,
  type SandboxPort,
} from '../../ui/rounds/sandbox-coordinator';
import { VersionMenu } from '../../ui/components/hud/VersionMenu';
import { versionSelectorListStyle } from '../../ui/rounds/sandbox-layout';
import {
  buildVersionMenu,
  type VersionMenuSort,
  type MenuCandidate,
  type VersionSummary,
} from '../../ui/rounds/version-menu-model';
import {
  clientStorePromptSeam,
  createRoundsPromptChannel,
  type RoundsPromptChannel,
} from './rounds-prompt';
import {
  DEFAULT_SANDBOX_SPEED,
  SANDBOX_SPEED_STEPS,
  admissibleSandboxSpeeds,
  effectiveSandboxSpeed,
  type SandboxSpeed,
} from '../../ui/rounds/run-submit-model';
// Constants only, from a leaf module with no imports of its own — never `practice-worker.ts`, which
// would pull the run driver and its quota path into this bundle to reach two integers.
import { DEFAULT_WATCH_WINDOW_TICKS, MIN_WATCH_SECONDS } from './watch-window';
import { detailPrefetchTicks } from '../../ui/rounds/detail-continuity';
import { createChatChannel } from './chat-channel';
import { createChatUncertaintyMarkerStore } from './chat-uncertainty-marker';
import { useTurnGateBridge } from './turn-gate-bridge';
import { usePlayheadLoop } from './playhead-loop';
import { createRoundsPhaseChannel, useRoundsPhase } from './rounds-phase';
import { rebindRound, type RoundEpochState } from './round-epoch';
import {
  createStandingsSource,
  type StandingsRead,
} from '../../net/rounds-standings';
import { deriveColonyRoster } from '../../ui/rounds/colony-placement';
import { roundResultReadable } from '../../ui/rounds/waiting-state';
import type { GradedColony } from '../../ui/components/rounds/ColonyRail';
import {
  createRoundsCredentialStore,
  type RoundsCredentialStore,
  type StorageLike,
} from './rounds-credential-store';
import {
  createPracticeCredentialStore,
  type PracticeCredentialStore,
  type PracticeRandom,
} from './practice-credential-store';
import {
  browserPracticeCapabilityProbe,
  type PracticeCapabilityProbe,
  type PracticeCapabilityReport,
} from './practice-capability';
import type {
  PracticeDocumentCompiler,
  PracticeRunRecord,
  PracticeTraceStore,
  PracticeWorkerFactory,
} from './practice-runner';
import type { HomePracticeEntry, PracticeBeginOutcome } from './practice-entry';
import { practiceMenuCandidates } from './practice-resume';
import { createPracticeRunController, type PracticeRunController } from './practice-run';
import { createPracticeWatcher, type PracticeWatcher } from './practice-watch';
import {
  createPracticeRunTransport,
  openPracticeSession,
  type PracticeSessionHandle,
} from '../../net/rounds-practice-http';
import { browserLockManager, type LockManagerLike } from './run-lease';
import {
  ROUNDS_LEADERBOARD_ROUTE,
  ROUNDS_PRACTICE_ROUTE,
  ROUNDS_VERSUS_ROUTE,
  RoundsRoutes,
} from './RoundsRoutes';
import { createSessionDemand, type SessionDemand } from './session-demand';
import {
  gradedSubjectFor,
  resolveReviewRound,
  roundIsNotGradedYet,
  roundsReviewPath,
} from './review-round-route';
import { RoundsReview } from '../../ui/screens/RoundsReview';

export interface RoundsControllerDeps {
  /** The API origin. `''` (the default) is same-origin — what production uses. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Durable credential storage. Production passes a guarded `window.localStorage` shim. */
  storage?: StorageLike;
  /** Pre-built credential store, for tests that want to observe it directly. */
  credentials?: RoundsCredentialStore;
  /** The name to mint under on a first visit. */
  displayName?: string;
  /** The app-root audio seam bundle, threaded to the in-match HUDs exactly as in live mode. */
  sound?: SoundControlSeams;
  /** Wall-clock source for the advisory prompt cooldown's countdown (**F3**) and for the sandbox's
   * playback loop. Defaults to `performance.now`; tests inject a `FakeClock` so a 60-second
   * cooldown costs no seconds and a minute of playback costs none either. */
  clock?: Clock;
  /** Timer seam for that countdown and the status watch's poll gaps. Defaults to `setTimeout`. */
  scheduler?: Scheduler;
  /** Animation-frame seam for the playhead loop (`playhead-loop.ts`). Distinct from `scheduler`,
   * which is a *timer* seam: a frame and a timeout are different cadences with different owners,
   * and collapsing them would make the poll interval and the playback rate the same decision. */
  frameScheduler?: FrameScheduler;
  /** The world-renderer factory for the review screen's frame (**D-156**). Production
   * threads the real PixiJS `CanvasRenderer`; omitted (jsdom tests, a bare mount) the
   * review frame renders its chrome with no canvas rather than needing a GPU context. */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => ReviewRenderer;
  /** The practice path's environment seams. See {@link PracticeEnvironment}. */
  practice?: PracticeEnvironment;
  /**
   * **The server's authentication identity state**, resolved by the composition root above this
   * one and passed straight through to Home (`client-app`, `client-home`).
   *
   * A **route seam with an inert default**, exactly as the shipped seams are: omitted, it is
   * `unresolved`, which every screen renders as its ungated fallback. No screen resolves it for
   * itself — one that did would own a transport, a failure policy and a retry policy the root
   * exists to own.
   */
  identity?: IdentityState;
  /** Begins an interactive sign-in. Owned by the composition root (it holds MSAL); Home renders
   * the control. Omitted, the affordance draws and reaches nothing — the inert-seam convention. */
  signIn?: () => void;
  /** Ends the authentication session and discards the locally held credential (design **D20**).
   * There is no server endpoint of this application to reach: this application issues no session. */
  signOut?: () => void;
}

/**
 * **The practice path's environment seams, owned separately from the tournament path's**
 * (`client-app`).
 *
 * Separate is the requirement, not the tidy option. A shared credential store would mean a
 * tournament credential the server rejects takes the player's practice colony with it, and would
 * let a **device-minted** practice credential be presented to the tournament mint — which is a
 * self-mintable key on a metered path, exactly what architecture review **F2** forbade. So this
 * bundle names its own storage, its own gate and its own device seams, and the root builds each
 * exactly once.
 *
 * Every field is optional and defaults to the real thing, in the `BootstrapDeps` mould: a test
 * replaces only what a jsdom process cannot provide. **Three** have no production default and
 * cannot: `createWorker`, `traceStore` and `compile` all cross the `wasm-bindgen` boundary, so they
 * are `waggle-wasm`'s to supply (`tasks.md` group 5). Until they exist the root offers practice,
 * states the device is capable, and refuses a *run* rather than fabricating a runner that pretends
 * to step a season — or, worse for `compile`, a gate that pretends every document compiles.
 */
export interface PracticeEnvironment {
  /** Durable storage for the practice credential. Defaults to the same guarded
   * `window.localStorage` shim the tournament store uses — under a **different key**, never the
   * same record. */
  storage?: StorageLike;
  /** Pre-built practice credential store, for a test that wants to observe it directly. */
  credentials?: PracticeCredentialStore;
  /** Randomness for the device-minted credential. Defaults to `crypto`. Run *seeds* no longer come
   * from here — they are server configuration, delivered in the run-start response. */
  random?: PracticeRandom;
  /** The device capability gate (**D-I**). Defaults to the real browser probe. */
  capabilities?: PracticeCapabilityProbe;
  /** The simulation worker factory (`rounds-local-run`). No default — see above. */
  createWorker?: PracticeWorkerFactory;
  /**
   * The **on-device** whole-colony compiler (`rounds-local-run` "The document is compiled locally").
   * No default — see above, and see {@link PracticeDocumentCompiler}: the only default available
   * would be "it compiles", which is exactly the request the requirement forbids sending.
   */
  compile?: PracticeDocumentCompiler;
  /** The device-local trace store (`rounds-local-trace-store`). No default — see above. */
  traceStore?: PracticeTraceStore;
  /**
   * **The run-lease facility** (`rounds-local-trace-store` "A run in progress is announced by a
   * lease"). Defaults to `navigator.locks` where the runtime has it, and to `undefined` where it does
   * not — which is a meaningful value, not a missing one: with no lease facility liveness cannot be
   * established, so nothing is reclaimed and no interrupted run is reported (`run-lease.ts`).
   *
   * A seam for the reason the other device seams are: jsdom provides no `navigator.locks` at all, so
   * without it the cross-tab scenarios could not be modelled — never mind bound.
   */
  locks?: LockManagerLike;
}

/** The route rounds mode plays on: the Colony slot IS the sandbox.
 *
 * `ROUNDS_HOME_ROUTE` used to live here, pointing at this same path, and the root moved the player
 * onto it as soon as a session opened. It is **deleted rather than repointed** (**D-b**): there is
 * no longer a route the root sends the player to on its own. `/` is the mode's entry screen and
 * the player leaves it by an explicit action — Resume, or a successful join. */
const SANDBOX_ROUTE = '/colony';

/** Where the leaderboard's Back leads — the mode's entry screen. Named rather than inlined so the
 * route vocabulary stays in one place; it is **not** a reinstated `ROUNDS_HOME_ROUTE`, because nothing
 * navigates here on the root's own initiative. */
const HOME_ROUTE = '/';

/** The standings route — where a membership with no sandbox of its own resumes to (**F3**). */
const STANDINGS_ROUTE = '/standings';

/**
 * How often a run in flight is asked how far it has written.
 *
 * A *cadence* is a wall-clock decision, so it lives here rather than in the coordinator, which
 * owns only the ordering of the answers (`poll-requested` → `poll-run` → `run-progress`, with a
 * sequence gate that discards a stale answer arriving after a newer one). Completion itself does
 * **not** depend on this: `awaitCompletion` is a one-shot long poll that resolves on its own, so
 * dropping every progress read would cost the written-extent track its movement and nothing else.
 */
const RUN_PROGRESS_POLL_MS = 500;

/** How often, and how many times, the root re-checks whether an outstanding detail window has
 * landed. Bounded so a tick whose records are genuinely absent stops the loop instead of spinning:
 * the world view stays on its coarse draw, which is a legitimate view rather than a failure. */
const DETAIL_RESIDENCY_PROBE_MS = 16;
const DETAIL_RESIDENCY_PROBES = 60;

/**
 * The rounds working document as a coordinator snapshot.
 *
 * `GET /colony` already carries the whole document, whether it parses, its diagnostics and its
 * version ordinal (`ColonyMeta`'s rounds-only fields), so the sandbox screen's editor is seeded
 * from the read the root already performs rather than from an extra activation of the head
 * version — which would relocate the head as a side effect of merely opening the screen.
 *
 * Returns `null` when the roster carries no document at all: a screen with no document is a real
 * state (a sandbox whose colony route has not answered yet), and an empty string in its place
 * would read as "your programs are gone".
 */
function documentSnapshotOf(meta: ColonyMeta): DocumentSnapshot | null {
  if (meta.document === undefined) {
    return null;
  }
  return {
    // `documentVersion` is absent on a surface that has never reported one. `0` is an ordinal no
    // sandbox issues, so it cannot be mistaken for a real version, and the coordinator's save path
    // treats it as "no known base" exactly as `rounds-prompt.ts` does.
    version: meta.documentVersion ?? 0,
    source: meta.document,
    valid: meta.documentValid !== false,
    diagnostics: meta.documentDiagnostics ?? [],
  };
}

/**
 * **The candidate picker's row label** (UI/UX audit — the picker shipped as bare "Candidate 1..4"
 * rows with no way to tell them apart beyond the order they were run in). `MenuCandidate` already
 * carries the two facts that actually distinguish one run from another — which version it was
 * submitted from, and what it scored — so this composes them rather than inventing a name the run
 * never had. `null` where a fact itself is: an unattributed run has no version, and a run still
 * in flight or one that failed has no score, and either renders as "Candidate N" alone rather than
 * a fabricated readout.
 */
function candidateSelectionLabel(candidate: MenuCandidate): string | null {
  const version = candidate.documentVersion === null ? null : `v${candidate.documentVersion}`;
  const score = candidate.score === null ? null : `scored ${candidate.score}`;
  const parts = [version, score].filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}

/** The bee-type dock's rows, in document order — which is also the order the extension palette is
 * indexed by, so a type's colour is stable for as long as the roster is. */
function typeViewsOf(meta: ColonyMeta | null): readonly SandboxTypeView[] {
  if (meta === null) {
    return [];
  }
  return meta.types.map((type) => ({
    typeId: type.typeId,
    name: type.name,
    buff: type.buffName,
    birthType: type.typeId === meta.birthType,
  }));
}

/**
 * The whole-colony program set a Run submits (**G7**) — the working **document** itself (design
 * `rounds-candidate-document` **D8**), never a per-type reassembly. The client holds the document
 * and sends exactly that; the server derives the roster and the birth type from it.
 *
 * `baseOrdinal` is the ordinal the document was edited from — `headVersion ?? versionHistory.
 * headOrdinal` at the call site, the same "coordinator's own head wins over the last fetched one"
 * precedence the version selector already uses (below), so a Run press never bases itself on a
 * value staler than what the screen is already showing.
 *
 * Returns `null` before the colony has ever loaded: there is no honest candidate to submit yet.
 */
function candidateFromDocument(
  source: string,
  baseOrdinal: number,
  meta: ColonyMeta | null,
): RunCandidate | null {
  if (meta === null) {
    return null;
  }
  return { document: source, baseOrdinal };
}

/**
 * The two **scope-independent** tournament reads (implementation review **F3**).
 *
 * `GET /rounds/tournament` and `GET /rounds/tournament/standings` are addressed absolutely and
 * never touch `RoundsHttpClient`'s `scope` — that is stated on the methods themselves, and it is
 * why the transport contract can say standings are readable by *every* member. Narrowed to exactly
 * these two so nothing can reach a sandbox route through a client that has no sandbox.
 */
type TournamentReads = Pick<RoundsTournamentTransport, 'fetchTournament' | 'fetchStandings'>;

/**
 * The scope a **tournament-read-only** client is constructed with.
 *
 * Deliberately not a well-formed sandbox id: a member with no sandbox has no sandbox route to call,
 * so a value that looked like one would be an invitation to call one. The narrow
 * {@link TournamentReads} type this client is handed out as is what actually enforces that; this
 * string only makes a mistaken call obvious in a request log.
 */
const TOURNAMENT_ONLY_SCOPE = 'tournament-read-only';

/**
 * **The bearer renewal every rounds client is built with** (E2E round-3 finding C3/C4).
 *
 * A session token lives in the server's in-memory `SessionRegistry` and a rounds mint persists no
 * session row, so a server restart — with the database entirely intact — disowns every bearer in
 * the room. The tournament credential does not: it is a durable row, and presenting it to the mint
 * is exactly what a page reload does to recover. That is the whole bug: this root restores **once**
 * at boot, so nothing ever presented the credential a second time and every tab in the room polled
 * `403 unauthorized` for ever, until someone reloaded it by hand.
 *
 * So the credential is handed to the transport as a *renewal*, and the transport spends it on the
 * one refusal that means the bearer is unknown.
 *
 * The three mint outcomes the boot restore already treats as **definitive** (`unknownCredential`,
 * `invalidCredential`, `unauthorized` — `rounds_http` splits a transient read error out as a `500`
 * precisely so a client can) are the ones that retire the bearer: the membership really is gone, so
 * minting again on the next poll would only produce another dead session. Everything else is
 * undecided, keeps the bearer, and may be tried again — a network blip landing in the same second
 * as the restart must not disable the recovery it is the recovery for.
 *
 * A module function rather than a hook: it closes over nothing but the DI bundle, which is injected
 * once and never reactive.
 */
function sessionRenewal(
  deps: RoundsControllerDeps,
  credential: string,
  displayName: string,
): () => Promise<BearerRenewal> {
  return async () => {
    const outcome = await mintRoundsSession({
      baseUrl: deps.baseUrl,
      fetchImpl: deps.fetchImpl,
      displayName,
      credential,
    });
    if (outcome.kind === 'minted') {
      return { kind: 'renewed', token: outcome.session.token };
    }
    return outcome.kind === 'unknownCredential' ||
      outcome.kind === 'invalidCredential' ||
      outcome.kind === 'unauthorized'
      ? { kind: 'refused' }
      : { kind: 'unavailable' };
  };
}

/**
 * The sandbox arm of the dispatching trace reader for a member who **has** no sandbox.
 *
 * A member with no sandbox can only ever address a `gradedRound` locator, so this arm is
 * unreachable. It reports a corruption rather than inventing a trace because `TraceReader.open`
 * must return a value — `rounds-review-source.ts` explains at length why there is no inert reader
 * and why every value `open` could invent is a lie.
 */
const NO_SANDBOX_TRACES: SandboxTraceClient = {
  createTraceReader: () => ({
    open: () =>
      Promise.resolve({
        kind: 'corrupt' as const,
        component: 'coarse' as const,
        message: 'this membership has no sandbox of its own',
      }),
  }),
};

interface RoundsBoot {
  session: RoundsSession;
  /**
   * The **sandbox**-scoped client, or `null` for a member who has no sandbox (a spectator).
   *
   * Nullable rather than absent-with-the-whole-boot, which is what **F3** found: `openMembership`
   * returned before setting `boot` at all when `session.sandbox` was null, so a spectator received
   * no phase channel, no standings source and no graded reader either — despite none of the three
   * needing a sandbox. The sandbox client is the *only* thing a spectator lacks.
   */
  client: RoundsHttpClient | null;
  /** The tournament reads every member has, spectator included. Same instance as {@link client}
   * when there is one — one client, one set of connections. */
  reads: TournamentReads;
  // No `discardedCredential` here (review follow-up): it was written once, always `false`, and never
  // read. The banner reads the React state of the same name, so a permanently-false field beside it
  // was a trap for the next reader — especially in an area where two "state never reset" defects
  // (F-30, F-31) have already been found.
}

/**
 * An open practice session — a **second, independent boot**, never a mode of {@link RoundsBoot}.
 *
 * The scope carries the `practice` domain tag, so the shipped `RoundsHttpClient` serves the colony,
 * the document write, the version history and the Beekeeper thread against it without a second
 * client type: a practice sandbox *is* a sandbox. What differs is the run, and only the run.
 */
interface PracticeBoot {
  session: PracticeSessionHandle;
  client: RoundsHttpClient;
  /**
   * The device-side Run seam, or `null` when this build has no simulation worker to run it with
   * (`tasks.md` group 5). `null` refuses a run and says why; it never fabricates one.
   */
  run: PracticeRunController | null;
}

// **No `practiceSeed` any more.** The seed a practice run simulates comes from server configuration
// in the run-start response, not from a device mint — the sandbox is a ranked ladder, so a field this
// device chose for itself would produce a score comparable with nobody's.

/**
 * A colony id the client may act on: a non-negative integer, or `null` for **absence**.
 *
 * Named and shared rather than inlined at the one call site, because the rule it enforces is the
 * general one implementation review **F4** turned on: a colony id is a valid domain value, so it can
 * never double as a sentinel for "we do not know". Absence has to stay structurally absent.
 */
function validColonyId(value: number | null): number | null {
  return value !== null && Number.isInteger(value) && value >= 0 ? value : null;
}

function windowLocalStorage(): StorageLike {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
}

/** Tab-scoped storage, for the one value that is deliberately **not** durable: the plaintext join
 * code (`client-home`). Read lazily inside the shim, like every other storage adapter here, so a
 * sandboxed `SecurityError` degrades to "nothing held" rather than throwing at boot. */
function windowSessionStorage(): StorageLike {
  return {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
    removeItem: (key) => window.sessionStorage.removeItem(key),
  };
}

/**
 * **The routes that demand no session at all.**
 *
 * `/leaderboard` reads a board that is gated by whatever gates the rest of the API and names no
 * membership; `/versus` is a live match on its own path. Neither consumes a tournament session, and
 * `client-app`'s rule is that nothing demands a session it would not use — so a mint on arrival at
 * either would be a request nothing reads.
 */
const SESSIONLESS_ROUTES: ReadonlySet<string> = new Set([
  ROUNDS_LEADERBOARD_ROUTE,
  ROUNDS_VERSUS_ROUTE,
]);

export function RoundsController(deps: RoundsControllerDeps): ReactElement {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathname = useLocation().pathname;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const [boot, setBoot] = useState<RoundsBoot | null>(null);
  /** The current boot, readable from the memoized-once entry-point closures. */
  const bootRef = useRef<RoundsBoot | null>(null);
  bootRef.current = boot;
  const [bootError, setBootError] = useState<string | null>(null);
  const [runs, setRuns] = useState<readonly ReviewRun[]>([]);
  // No `reviewRound` state any more (`client-rounds-review`): the round being reviewed is carried by
  // the route and nowhere else. A second copy of it is not merely redundant — a stored marker and the
  // address bar can disagree after a history traversal, a bookmark or a shared link, and a route
  // cannot disagree with itself.
  const [reviewViewer, setReviewViewer] = useState<{
    round: number;
    state: GradedReviewViewerState;
  } | null>(null);
  const [standingsRead, setStandingsRead] = useState<StandingsRead | null>(null);
  const [dismissedDefaultingRounds, setDismissedDefaultingRounds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  /** The projected gathering QR still targets the public root. Capture its secret before removing
   * it from browser history, then hand it to the dedicated join effect below; Home remains a
   * three-action player screen and never regains an operator form. */
  const [scannedJoinCode] = useState(() => readJoinParam(window.location));
  const qrJoinAttemptedRef = useRef(false);
  const [qrJoinNotice, setQrJoinNotice] = useState<string | null>(null);
  useEffect(() => {
    stripJoinParam(window.location, window.history);
  }, []);
  /** A stored membership was refused and forgotten — worth saying, since it is a real loss, but
   * never an error state: Home is fully usable underneath it. */
  const [discardedCredential, setDiscardedCredential] = useState(false);
  /**
   * How the boot's restore ended, when it ended **undecided** (UI/UX audit **F-12**).
   *
   * Two states, not the one boolean this replaces: that boolean showed *"Couldn't reach the hive"* for
   * both a transport failure and a `5xx`, and the second is not a network problem — the hive was
   * reached and failed to answer properly. Only `unreachable` is about the network.
   *
   * A **refused** restore is deliberately not a state here. Since the server stopped folding transient
   * read errors into `403`, a refusal is definitive, so it clears the credential and falls into the
   * existing `discardedCredential` banner rather than sitting behind a prompt that would reappear on
   * every boot until dismissed.
   *
   * The credential is kept in both of these, and no automatic retry is issued: **Retry** is the
   * player's, because a boot that silently re-asks is what cost two failed requests per load.
   */
  const [restoreState, setRestoreState] = useState<'none' | 'server-error' | 'unreachable'>('none');
  /**
   * The open practice session, or `null` — which is also the whole mode fork for the sandbox slot.
   *
   * Practice sits **beside** the tournament rather than inside it (`rounds-tournament-state`'s
   * one-active-tournament rule makes anything else a hazard), so this is a second, independent boot:
   * its own scope, its own token, its own client. A tournament membership can be restored and
   * resumable underneath it, untouched.
   */
  const [practiceBoot, setPracticeBoot] = useState<PracticeBoot | null>(null);
  /** The practice colony's roster and document. Held here rather than in the client store's single
   * `colonyMeta` slice, so opening practice cannot overwrite a restored tournament sandbox's roster
   * — the two are separate sessions and must not share the one piece of state. */
  const [practiceColony, setPracticeColony] = useState<ColonyMeta | null>(null);
  /**
   * Runs this device began and never finished, discovered on the practice restore.
   *
   * Component state and nothing more — no durable flag anywhere (design **D1**). It does not need to
   * survive a reload, because the thing that stops the notice repeating is that the run's bytes were
   * *reclaimed* when it was first reported, not that we wrote down having said it.
   */
  const [interruptedRuns, setInterruptedRuns] = useState<readonly number[]>([]);

  // The credential store is a DI singleton: reading `deps.storage` on every render would mint a
  // fresh adapter each time, and this one owns the only secret that cannot be re-derived.
  const credentialsRef = useRef<RoundsCredentialStore | null>(null);
  if (credentialsRef.current === null) {
    credentialsRef.current =
      deps.credentials ?? createRoundsCredentialStore(deps.storage ?? windowLocalStorage());
  }
  /**
   * **The practice seams, constructed here and nowhere else** (`client-app` "The root owns the
   * practice seams and no screen constructs them").
   *
   * A DI singleton for the same reason the tournament credential store is one: reading `deps` on
   * every render would mint a fresh adapter each time, and the credential store owns the only key to
   * this device's practice colony. The capability probe joins it because a gate re-created per render
   * would re-probe OPFS on every keystroke.
   *
   * The store is built over its **own key** by construction — `createPracticeCredentialStore`'s
   * default — so nothing here can reach the tournament record even when both share one `Storage`.
   */
  const practiceSeamsRef = useRef<{
    credentials: PracticeCredentialStore;
    capabilities: PracticeCapabilityProbe;
    createWorker: PracticeWorkerFactory | undefined;
    compile: PracticeDocumentCompiler | undefined;
    traceStore: PracticeTraceStore | undefined;
    locks: LockManagerLike | undefined;
  } | null>(null);
  if (practiceSeamsRef.current === null) {
    const env = deps.practice ?? {};
    practiceSeamsRef.current = {
      credentials:
        env.credentials ??
        createPracticeCredentialStore(env.storage ?? deps.storage ?? windowLocalStorage(), env.random),
      capabilities: env.capabilities ?? browserPracticeCapabilityProbe(),
      createWorker: env.createWorker,
      compile: env.compile,
      traceStore: env.traceStore,
      // `??` deliberately, not `?? undefined`: a caller that supplies no lease seam gets the real
      // one, and a runtime with no Web Locks resolves to `undefined` inside `browserLockManager`.
      locks: env.locks ?? browserLockManager(),
    };
  }
  /** The capability report, resolved **once** — before any practice affordance leads anywhere. Held
   * in a ref rather than state because it never changes within a load and nothing renders from it
   * directly: the entry point below awaits it. */
  const capabilityRef = useRef<Promise<PracticeCapabilityReport> | null>(null);

  // Guards state writes after the root has gone.
  const liveRef = useRef(true);
  /**
   * The current {@link restorePractice}, for the same reason {@link restoreRef} exists: the demand
   * guards are built once, in a ref, so they must reach the freshest closure rather than capture the
   * first render's.
   */
  const restorePracticeRef = useRef<(() => Promise<void>) | null>(null);
  /**
   * Which membership operation currently owns the credential store.
   *
   * Home is required to be interactive **while** the boot's credential restore is in flight, which
   * means a player can join a new membership before the restore answers. Bumping this the instant a
   * join begins makes the in-flight restore stale, so it writes nothing: without it the late
   * restore would persist the *old* credential over the new one and re-open the old membership,
   * stranding a freshly minted membership that still occupies a roster slot (implementation
   * review). The two paths are now ordered rather than racing.
   */
  const membershipEpochRef = useRef(0);
  /** The current {@link runRestore}, so the once-memoized `homeEntry.join` can re-run a restore it
   * displaced without capturing a stale closure. Assigned every render, beside its definition. */
  const restoreRef = useRef<(() => Promise<void>) | null>(null);
  /** The restore currently in flight, or `null`. The membership epoch cannot arbitrate between two
   * *concurrent* restores — they share it — so this is what keeps them from both applying. */
  const restoreInFlightRef = useRef<Promise<void> | null>(null);
  /** The active round identity. Unlike the membership epoch, this advances while the membership
   * itself stays mounted and invalidates every round-scoped async response. */
  const roundEpochRef = useRef<RoundEpochState>({ round: 0, epoch: 0 });

  /**
   * Opens everything a live membership needs: the one `RoundsHttpClient`, the editor's seed, and
   * the run history. Shared by the two — and only two — occasions a membership arrives (**D-c**):
   * a background **restore** at boot, and an explicit **join** from Home.
   *
   * It deliberately does **not** navigate. Where the player goes next is the caller's decision,
   * because the two occasions differ: a join opens the sandbox the player just asked for, while a
   * restore only publishes Resume and waits (**D15**).
   */
  const openMembership = useCallback(
    async (session: RoundsSession): Promise<void> => {
      // Stamped at entry and re-checked after every await: a newer membership operation that
      // starts mid-flight must win, and a stale one must not publish its colony or run history
      // over the newer one's.
      const epoch = membershipEpochRef.current;
      // **The membership and its read surfaces are opened independently of the sandbox**
      // (implementation review **F3**). This used to return here when `session.sandbox` was null,
      // *before* `boot` or `membership` were set — so a spectator received no tournament client, no
      // phase channel, no standings source and no graded trace reader, none of which need a
      // sandbox, and the stored credential made every reload repeat that same unusable state. What
      // a spectator genuinely lacks is the sandbox-scoped client below, and with it the
      // coordinator, the prompt channel and the chat channel.
      const client =
        session.sandbox === null
          ? null
          : new RoundsHttpClient({
              baseUrl: deps.baseUrl ?? '',
              scope: session.sandbox,
              token: session.token,
              renewToken: sessionRenewal(deps, session.credential, session.displayName),
              fetchImpl: deps.fetchImpl,
            });
      const reads =
        client ??
        new RoundsHttpClient({
          baseUrl: deps.baseUrl ?? '',
          scope: TOURNAMENT_ONLY_SCOPE,
          token: session.token,
          renewToken: sessionRenewal(deps, session.credential, session.displayName),
          fetchImpl: deps.fetchImpl,
        });
      roundEpochRef.current = { round: session.round, epoch: roundEpochRef.current.epoch + 1 };
      setDismissedDefaultingRounds(new Set());
      const roundEpoch = roundEpochRef.current.epoch;
      // **A membership arriving retires every banner about not having one** (review follow-up).
      //
      // Neither state was ever cleared: `discardedCredential` had no reset path at all, and
      // `restoreState` was cleared only by the Retry button's own handler. Both banners are
      // `position: fixed; z-index: 900`, so a failed or discarded boot restore left a notice floating
      // over the **sandbox** the player then joined — and the discard copy ("Your previous tournament
      // couldn't be reopened. Join again with a code when you have one.") is actively false once they
      // have. Only a reload cleared it.
      //
      // Cleared here rather than in each caller because this is the one funnel both occasions pass
      // through — a boot restore and an explicit join — and it is the point at which the membership is
      // *published*, so a stale call that loses the epoch check above never reaches it.
      setDiscardedCredential(false);
      setRestoreState('none');
      setBoot({ session, client, reads });
      if (client === null) {
        // No sandbox: no roster to seed and no runs to list. Everything above — the tournament
        // reads — is already wired.
        return;
      }

      // Seed the editor: in live mode the roster arrives as a `ColonyMeta` message, and there is
      // no socket here to carry one.
      const colony = await client.fetchColony();
      if (
        !liveRef.current ||
        membershipEpochRef.current !== epoch ||
        roundEpochRef.current.epoch !== roundEpoch
      ) {
        return;
      }
      if (colony === null) {
        setBootError('Your colony could not be read — the sandbox is open but has no roster yet.');
      } else {
        useClientStore.getState().applyColonyMeta(colony);
      }

      const history = await client.listReviewRuns();
      if (
        liveRef.current &&
        membershipEpochRef.current === epoch &&
        roundEpochRef.current.epoch === roundEpoch
      ) {
        setRuns(history);
      }
    },
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** What a restore outcome does to this screen. One place, so boot and **Retry** cannot diverge. */
  const applyRestoreOutcome = async (
    outcome: Awaited<ReturnType<typeof restoreRoundsSession>>,
  ): Promise<void> => {
    if (outcome.kind === 'abandoned') {
      // The player joined from Home before this answered. The join owns the credential store and
      // the screen; this restore says and does nothing.
      return;
    }
    if (outcome.kind === 'none') {
      // Absent, or definitively refused and forgotten. Either way Home stands in its
      // no-membership state with join and create offered — a usable screen, not a dead end, and
      // **no** credential-free retry, which the surface would refuse.
      setDiscardedCredential(outcome.discardedCredential);
      redirectHomeIfStrandedOnSandbox();
      return;
    }
    if (outcome.kind === 'failed') {
      // We could not ask, or the hive failed to answer properly. Nothing is claimed about the
      // credential, which is kept. `unavailable` is the transport case; everything else reaching
      // here is a server-side failure, which must not borrow the transport copy.
      setRestoreState(outcome.outcome.kind === 'unavailable' ? 'unreachable' : 'server-error');
      redirectHomeIfStrandedOnSandbox();
      return;
    }
    await openMembership(outcome.session);
  };

  /**
   * **The blank-page fix.** A restore that settles with nothing to show used to leave the sandbox
   * route showing `sandbox-pending` forever: with no membership, `coordinator` never stops being
   * `null`, so nothing ever mints the roster that would let the screen move past the boot notice.
   *
   * **Narrowed to the *tournament* sandbox route** (`rounds-refresh-restore` task 4.7). It used to
   * fire on `/colony` for a practice player too, because the two sandboxes shared that one path and
   * practice never restored itself at boot — so a redirect to Home was the only path back for them.
   * Both halves of that have changed: practice has its own route and restores itself on it, so a
   * *tournament* restore settling with nothing has no business moving a practice player, and this now
   * cannot reach `/practice` at all.
   *
   * Still guarded to a sandbox path, because that is the only slot that can go permanently blank this
   * way — Home, Standings and Review all render regardless of `coordinator`. Never called from the
   * successful-open branch above, so a restore that *does* find a membership never redirects.
   */
  const redirectHomeIfStrandedOnSandbox = (): void => {
    if (pathnameRef.current === SANDBOX_ROUTE) {
      // **`replace`, not a push** (implementation review **F5**). The redirect corrects an address
      // that cannot be rendered; it is not a place the player chose to be. Pushed, Back walked
      // straight back into the pending dead end — and permanently, because the demand guard has
      // already `attempted`, so the restore that fired this redirect never runs a second time.
      navigateRef.current('/', { replace: true });
    }
  };

  /**
   * One restore attempt. Extracted from the boot effect (UI/UX audit **F-12**) so the **Retry**
   * control on the restore banner runs exactly the same path the boot did — a second, slightly
   * different retry implementation is how the two would drift.
   *
   * Note what is *not* here: any automatic re-attempt. A refused restore is retried only when the
   * player asks, which is what stops a credential the server will refuse from costing two failed
   * requests on every single load.
   */
  const runRestore = async (): Promise<void> => {
    // **One restore at a time** (review follow-up). `isCurrent` cannot separate two *concurrent*
    // restores: it compares against the membership epoch, and concurrent attempts share it, so both
    // would pass and both would apply — two mints, and two `openMembership` calls each building its
    // own clients and re-seeding the editor.
    //
    // The reachable interleaving is the one the F-30 hand-back created: a **Retry** is in flight, the
    // player submits a join that fails, and the failure re-runs the restore. (The two paths that look
    // more obvious are not reachable — the banner carrying Retry only exists *after* a restore has
    // resolved, and it unmounts on click, so neither a double-Retry nor a Retry during the boot
    // restore can happen.)
    //
    // Callers **coalesce onto** the in-flight attempt rather than being dropped: Retry's intent is
    // "make sure a restore happens", and one already is, so awaiting it satisfies the request without
    // a second round trip.
    const inFlight = restoreInFlightRef.current;
    if (inFlight !== null) {
      return inFlight;
    }
    const attempt = (async (): Promise<void> => {
      // **Restore, never mint** (**D-c**). Home is already mounted and interactive by the time
      // this resolves; nothing here blocks the first paint, and nothing here navigates.
      const bootEpoch = membershipEpochRef.current;
      const outcome = await restoreRoundsSession({
        baseUrl: deps.baseUrl,
        fetchImpl: deps.fetchImpl,
        credentials: credentialsRef.current!,
        // Consulted immediately before the restore writes anything.
        isCurrent: () => membershipEpochRef.current === bootEpoch,
      });
      if (!liveRef.current) {
        return;
      }
      await applyRestoreOutcome(outcome);
    })();
    restoreInFlightRef.current = attempt;
    try {
      await attempt;
    } finally {
      // Cleared whether it resolved or threw, so a failed attempt can never wedge Retry shut.
      restoreInFlightRef.current = null;
    }
  };
  // Always the freshest closure, so `homeEntry.join` — memoized once over DI — can re-run a restore
  // its own epoch bump displaced (see the ownership hand-back there). Same idiom as `bootRef` above.
  restoreRef.current = runRestore;

  /**
   * **Retry** discards the settled outcome and demands again.
   *
   * Through the guard rather than around it: calling `runRestore` directly would work today and would
   * leave the guard believing the session had settled, so a later consumer's demand would be a no-op
   * over a stale answer.
   */
  const retryRestore = async (): Promise<void> => {
    demandsRef.current?.tournament.reset();
    await demandsRef.current?.tournament.demand();
  };

  /** The host mint currently in flight, so a create followed immediately by Continue opens one
   * host session rather than two. */
  /**
   * **The two sessions' restore guards** (`client-app` "Session restore is per-session, idempotent
   * and demand-driven").
   *
   * Built once, in refs, because they hold the "has this been attempted" state that must survive
   * every re-render and every route change — which is precisely what a mount effect could not do
   * across two independent sessions.
   *
   * The tournament attempt is `runRestore` unchanged, wrapped rather than rewritten: it already owns
   * the epoch arbitration, the `restoreInFlightRef` coalescing and the F-30 hand-back, and none of
   * that is this change's to relitigate. What is new is *who may call it and when*.
   */
  const demandsRef = useRef<{
    tournament: SessionDemand;
    practice: SessionDemand;
  } | null>(null);
  if (demandsRef.current === null) {
    demandsRef.current = {
      tournament: createSessionDemand(async () => {
        await restoreRef.current?.();
        // `bootRef` is the settled fact: a membership was opened, or it was not. Read after the
        // attempt rather than threaded out of it, so the two cannot disagree.
        return bootRef.current !== null ? 'restored' : 'absent';
      }),
      practice: createSessionDemand(async () => {
        // The **guarded** restore, which shares its in-flight slot with the player-initiated open —
        // the two can genuinely run at once, and only one may reach the wire.
        await restorePracticeRef.current?.();
        return practiceBootRef.current !== null ? 'restored' : 'absent';
      }),
    };
  }
  const demands = demandsRef.current;

  /**
   * **Every consumer that needs a session asks for it** (tasks 3.5).
   *
   * This replaces a mount effect that restored the tournament session and nothing else, once, keyed to
   * whichever route the browser happened to boot on. That shape had a permanent failure mode: a player
   * who booted on the practice path got no tournament restore, and because the guard was once-only,
   * navigating to `/` afterwards could not run one either — so Home told them they had no membership
   * while a valid credential sat in storage, and only a reload fixed it.
   *
   * Keyed to the **route** now, and re-run when the route changes. Both guards are idempotent, so a
   * player crossing four routes issues at most one attempt per session, and a StrictMode double-invoke
   * issues none extra.
   *
   * Which routes demand which session is deliberate rather than blanket: Home offers Resume for a
   * tournament membership, so it demands the tournament session; `/practice` is a practice surface, so
   * it demands the practice one; `/colony`, `/standings` and the review route are tournament surfaces.
   * Nothing demands a session it would not use — that is what keeps "no request before the player
   * acts" true for the player who only ever practises.
   */
  useEffect(() => {
    liveRef.current = true;
    if (pathname === ROUNDS_PRACTICE_ROUTE) {
      void demands.practice.demand();
    } else if (!SESSIONLESS_ROUTES.has(pathname)) {
      void demands.tournament.demand();
    }
    // **Neither new route demands a session**, and that follows the same rule the two above do rather
    // than making an exception to it — see {@link SESSIONLESS_ROUTES}.
    //
    // **Home does not demand the practice session** either, deliberately. Nothing on Home reads it:
    // the practice affordance opens a session on the player's act. Demanding it here would issue a
    // resume-only request on every load for anyone who has ever practised — a request nothing
    // consumes, on a surface whose whole posture is that no request precedes the player acting.
    return () => {
      liveRef.current = false;
    };
    // `deps` is a DI bundle injected once by the caller, not reactive props; `demands` is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /** A scan is an explicit tournament-join intent. It mints once under StrictMode and opens the
   * sandbox without making tournament plumbing part of Home's normal player surface. */
  useEffect(() => {
    if (scannedJoinCode === null || qrJoinAttemptedRef.current) return;
    qrJoinAttemptedRef.current = true;
    setQrJoinNotice('Joining the tournament…');

    const priorEpoch = membershipEpochRef.current;
    const ownedEpoch = priorEpoch + 1;
    membershipEpochRef.current = ownedEpoch;
    void (async () => {
      const outcome = await joinRoundsSession({
        baseUrl: deps.baseUrl,
        fetchImpl: deps.fetchImpl,
        credentials: credentialsRef.current!,
        displayName: deps.displayName,
        joinCode: scannedJoinCode,
      });
      if (!liveRef.current) return;
      if (outcome.kind !== 'minted') {
        if (membershipEpochRef.current === ownedEpoch) {
          membershipEpochRef.current = priorEpoch;
        }
        setQrJoinNotice(mintFailureMessage(outcome));
        return;
      }
      await openMembership(outcome.session);
      if (liveRef.current) {
        setQrJoinNotice(null);
        navigateRef.current(outcome.session.sandbox === null ? HOME_ROUTE : SANDBOX_ROUTE);
      }
    })();
    // This is a one-shot boot input; the injected dependencies are stable for this root's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedJoinCode]);

  // The Run seam: the client itself, with the review rail refreshed off the run's own completion.
  // The refresh is the only thing wrapped — nothing here navigates (**D7**).
  //
  // Both seams are memoized on the client, not rebuilt per render: `RoundsReview` re-opens the
  // trace whenever its `reader` identity changes, so a fresh reader on every render would re-fetch
  // a manifest, a coarse tier and a detail window each time the rail refreshed.
  /** The sandbox-scoped client, as `undefined` rather than `null` — every seam below reads it as
   * "there is no sandbox client", and one spelling for that is enough. */
  const client = boot?.client ?? undefined;
  const colonyRun: ColonyRunController | undefined = useMemo(
    () =>
      client === undefined
        ? undefined
        : {
            submit: (candidate) => client.submit(candidate),
            awaitCompletion: async (runId) => {
              /**
               * **Both epochs, captured where the wait begins** (implementation review **F5**).
               *
               * A completion is the longest-lived request this screen makes — a whole batch run —
               * so it routinely outlives the round it was started in. Guarded only on component
               * liveness, a pending old-scope completion resolving *after* a round rebind had
               * cancelled the old coordinator and cleared `runs` would list the previous round's
               * runs on the previous round's client and write them in as the current round's.
               *
               * Checked twice, deliberately: before the history is *fetched*, so a retired round
               * does not even ask, and again before it is *published*, because the rebind can also
               * land during that second request.
               */
              const membershipEpoch = membershipEpochRef.current;
              const roundEpoch = roundEpochRef.current.epoch;
              const current = (): boolean =>
                liveRef.current &&
                membershipEpochRef.current === membershipEpoch &&
                roundEpochRef.current.epoch === roundEpoch;
              const outcome = await client.awaitCompletion(runId);
              if (outcome.kind === 'completed' && current()) {
                void client.listReviewRuns().then((history) => {
                  if (current()) {
                    setRuns(history);
                  }
                });
              }
              return outcome;
            },
          },
    [client],
  );
  const tournamentClient = useMemo(
    () =>
      boot === null || boot.session.tournamentId.length === 0
        ? undefined
        : new TournamentReadClient({
            baseUrl: deps.baseUrl ?? '',
            tournamentId: boot.session.tournamentId,
            credential: boot.session.credential,
            fetchImpl: deps.fetchImpl,
          }),
    // The credential and tournament identity arrive together in `boot`; DI is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boot?.session.tournamentId, boot?.session.credential],
  );
  // The graded arm needs only the tournament credential, so a member with no sandbox still gets a
  // reader (**F3**); the sandbox arm they can never address is the inert one above.
  const reader = useMemo(
    () =>
      tournamentClient === undefined
        ? undefined
        : createDispatchingTraceReader(client ?? NO_SANDBOX_TRACES, tournamentClient),
    [client, tournamentClient],
  );

  // --- Home's entry points ------------------------------------------------------------------
  //
  // **The tournament entry points are gone from here** (`home-and-single-player`). Home used to be
  // handed a `join`, a `create`, a `hostRebind`, an `enterHostFlow` and a `resume`, and it rendered a
  // control for each. All five are relocated whole to the `/demo` composition root, which already
  // owned the create-and-host bootstrap and `recoverHostCredential` — and host recovery in particular
  // had to survive somewhere, because a member credential is stored as a hash and is unrecoverable.
  //
  // The tournament *stack* below is untouched: `openMembership`, the restore guard, the sandbox
  // client, standings and the graded review all still compile and are still tested. What was removed
  // is one screen's wiring into it, not the subsystem.
  //
  // What Home is handed now is the practice entry below, plus two navigations — the computer match and
  // the leaderboard — which are composed at the render site because they close over nothing.

  // --- Home's practice entry points (`client-practice`, `client-app`) ---------------------
  //
  // The gate first, then the sandbox. **No practice session is created for a device that failed the
  // gate** — which is why the capability report is awaited here rather than consulted after an open:
  // an open would already have provisioned a sandbox by then.
  //
  // Note what is *not* here: no join code, and no `POST /rounds/session`. Practice mints no
  // tournament membership, and a tournament membership is neither required nor disturbed
  // (`client-home` "Practice needs no join code and mints no membership").
  const practiceBootRef = useRef<PracticeBoot | null>(null);
  // **There is no `practiceConfigRef` any more** (`home-and-single-player`). It held the frozen
  // `(rivals, seed)` a player chose on Home, and there is nothing left in it: rivals are fixed at three
  // in the engine and the seed is server configuration, delivered in the run-start response. Its
  // recovery-on-resume (`practiceResumeConfig`) went with it — there is no per-session configuration
  // to recover.
  /** One watcher per watched run — the accumulator for the worker's in-flight deltas. A ref rather
   * than state: it is written from a worker message on every cadence boundary, and a `setState` there
   * would re-render the whole root thirty times a run for a value only the coordinator reads. */
  const practiceWatcherRef = useRef<PracticeWatcher | null>(null);

  /**
   * **Tell the player, once, that a run did not finish — and reclaim its bytes** (`client-practice`
   * "A run interrupted by a reload is reported once, and its staged bytes are reclaimed").
   *
   * An interrupted run is unrecoverable by design: the simulation runs in a worker that dies with the
   * page and writes no resumable checkpoint. What must not happen is that it vanishes *silently* — the
   * player comes back to a sandbox holding one fewer run than they remember with no account of where
   * it went.
   *
   * Two decisions are doing the work here.
   *
   * **Detection consults the lease, never the directory.** A staged generation is created eagerly,
   * before a byte is written, so its existence is evidence of nothing; and two tabs share one device
   * credential and therefore one store. Reporting on the directory alone would tell a player their run
   * had failed while it was running perfectly well in their other tab. `listAbandonedRuns` is the
   * lease-gated question, and it excludes a live generation, a generation whose liveness cannot be
   * established, and a *published* run whose staged copy a failed removal left behind.
   *
   * **Reclaiming is the acknowledgement.** There is no durable "already told them" flag — design
   * **D1** forbids new persisted state, and OPFS is durable enough that a flagless, unreclaimed
   * generation would re-report on every boot for the life of the device. So the notice is retired by
   * removing the bytes it is about, and the two cannot drift apart because they are the same act. If
   * the reclaim fails (another tab took the lease in between), nothing is reported either — the run is
   * still someone's, and it will be reported on a later reopen when it truly is not.
   *
   * Called from the **restore path**, not from store open: the writer store's open-time sweep is a
   * backstop for a generation the main thread never saw, and it has no player to tell.
   */
  const reportInterruptedRun = async (
    store: PracticeTraceStore | undefined,
    completedRuns: readonly PracticeRunRecord[],
  ): Promise<void> => {
    if (store?.listAbandonedRuns === undefined || store.reclaim === undefined) {
      // A build with no device store (no `waggle-wasm`) has no runs and nothing to report. Reporting
      // nothing is the honest answer, not a degraded one.
      return;
    }
    const abandoned = await store.listAbandonedRuns();
    if (!liveRef.current || abandoned.length === 0) {
      return;
    }
    // Reclaim first, report second. The reclaim is what makes the report non-repeating, so a report
    // shown without one would come back on every boot — and a reclaim that could not be taken means
    // the run is not ours to speak about.
    const reclaimed: number[] = [];
    for (const runId of abandoned) {
      if (await store.reclaim(runId)) {
        reclaimed.push(runId);
      }
    }
    if (!liveRef.current || reclaimed.length === 0) {
      return;
    }
    setInterruptedRuns((current) => [...current, ...reclaimed]);
    void completedRuns;
  };

  /**
   * **Restore the practice session from the device's stored credential — minting nothing**
   * (`client-practice` "A practice session is restored on its own route and mints nothing to do so";
   * design **D-c**).
   *
   * The sibling of {@link openPractice}, and deliberately a *different function* rather than a flag on
   * it, because the two differ in the one respect that matters most: an open is the **player's
   * explicit act** on Home and may create a sandbox; a restore is the **browser reloading a URL** and
   * must never create one. A boolean parameter would put both behaviours behind one call site and make
   * the dangerous one the easier mistake.
   *
   * Three restraints, each closing a way this could quietly cost a player their colony:
   *
   *   1. **`credentials.get()`, never `ensure()`.** With no stored credential this issues **no request
   *      at all** — the mint stays where `client-home` puts it, at the player's act.
   *   2. **`resumeOnly`.** The server declines to provision for an unrecognised credential and answers
   *      `absent`, so nothing is created behind a restore.
   *   3. **A `created` outcome is not adopted.** Belt and braces against (2): if a server without the
   *      flag ever answers `created` here, the honest reading is still "this is not the session I was
   *      restoring", and a fresh empty colony must not be presented as the player's own work.
   */
  const restorePractice = useCallback(async (): Promise<void> => {
    const seams = practiceSeamsRef.current!;
    /**
     * **A session that is already open needs no restoring** — checked here and again after every
     * await below.
     *
     * The reachable interleaving is a real one, not a theoretical tidy-up: Home demands the practice
     * session on arrival, and that demand is still resolving while the player taps "Play in sandbox",
     * which runs {@link openPractice}. Without this the restore would come back afterwards, issue a
     * *second* open for the same credential, and replace the boot the player's own act had just
     * established — two requests, and a `practiceBoot` identity swapped underneath a mounted screen.
     *
     * The demand guard cannot catch this: it serialises demands against each other, and `openPractice`
     * is not a demand — it is the player acting, and it is deliberately allowed to run whenever they
     * press the control.
     */
    const alreadyOpen = (): boolean => practiceBootRef.current !== null;
    /**
     * **A practice restore that settles with nothing takes the player to Home** (`client-app` "A
     * reload with nothing restorable for that route lands on Home"; implementation review **F1**).
     *
     * The sibling of {@link redirectHomeIfStrandedOnSandbox}, which task 4.7 narrowed to `/colony` so
     * a *tournament* restore would stop moving a practice player — and nothing then gave the practice
     * restore the equivalent. Its settling-with-nothing paths simply returned, leaving `coordinator`
     * `null` for the life of the page and `/practice` showing "Waking the hive…": a notice with no
     * control on it, no standings link and no way back.
     *
     * Two guards, both load-bearing. The **path** guard, because a restore that resolves after the
     * player has navigated away must not drag them back; and the **boot** guard, because a concurrent
     * `openPractice` (the player's own act on Home) may have settled a session while this restore was
     * in flight, and it must not be redirected out of it.
     *
     * `replace`, per **F5**: this corrects an address that cannot be rendered, so it has no business
     * in the back stack.
     */
    const redirectHomeIfStrandedOnPractice = (): void => {
      if (
        liveRef.current &&
        pathnameRef.current === ROUNDS_PRACTICE_ROUTE &&
        practiceBootRef.current === null
      ) {
        navigateRef.current('/', { replace: true });
      }
    };
    if (alreadyOpen()) {
      return;
    }
    const record = seams.credentials.get();
    if (record === null) {
      // Nothing stored: nothing to restore, and — the assertion this path is written for — no
      // request. A player who has never practised must not have a sandbox minted for them by the
      // act of loading a URL. They go to Home, where the mint lives.
      redirectHomeIfStrandedOnPractice();
      return;
    }
    capabilityRef.current ??= seams.capabilities.evaluate();
    const report = await capabilityRef.current;
    if (!report.supported || !liveRef.current || alreadyOpen()) {
      // A device that cannot run practice at all cannot restore a session either — the same dead end
      // as the two below, reached one step earlier, so it gets the same way out. The refusal copy
      // stays where it is, at Home's affordance.
      redirectHomeIfStrandedOnPractice();
      return;
    }
    const outcome = await openPracticeSession({
      baseUrl: deps.baseUrl,
      fetchImpl: deps.fetchImpl,
      credential: record.credential,
      resumeOnly: true,
    });
    if (!liveRef.current || alreadyOpen() || outcome.kind !== 'resumed') {
      // `absent`, `expired`, `created`, `unsupported`, `unavailable` — none of them is a restoration.
      // Only `resumed` says "this is the sandbox that credential names, and it is still here".
      redirectHomeIfStrandedOnPractice();
      return;
    }

    const boot = openPracticeBoot(outcome.session);
    setPracticeBoot(boot);
    practiceBootRef.current = boot;

    const colony = await boot.client.fetchColony();
    if (liveRef.current && practiceBootRef.current === boot && colony !== null) {
      setPracticeColony(colony);
    }

    // The device's own run index — read for the interrupted-run report and nothing else now that there
    // is no frozen configuration to recover from it.
    const localRuns = (await seams.traceStore?.listRuns()) ?? [];
    if (!liveRef.current || practiceBootRef.current !== boot) {
      return;
    }
    await reportInterruptedRun(seams.traceStore, localRuns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * **The practice session's single in-flight slot** (`client-practice` "Concurrent demands coalesce
   * onto the attempt already in flight"; tasks 4.6).
   *
   * Three callers can want a practice session, and the tournament path's demand guard cannot arbitrate
   * between them because two of them are not demands at all:
   *
   *   * the **restore** on `/practice`, which is a demand;
   *   * the player pressing **"Play in sandbox"**, which is an act and must work whenever they press
   *     it — including twice;
   *   * **"Start over"**, which is exempt (below).
   *
   * The practice open had no guard of any kind, so two of these in flight together meant two
   * `POST /rounds/practice/session` calls for the same credential and two boots, with whichever
   * resolved last replacing the other underneath a mounted screen. This slot makes the second caller
   * *join* the first.
   */
  const practiceInFlightRef = useRef<Promise<void> | null>(null);

  /**
   * Runs `work` in the shared slot, or joins whatever is already in it.
   *
   * Returns whether this call **ran** the work (`true`) or joined someone else's (`false`) — the
   * caller needs to know, because a joiner has no outcome of its own to report and must read the
   * settled session instead.
   */
  const inPracticeSlot = useCallback(async (work: () => Promise<void>): Promise<boolean> => {
    const inFlight = practiceInFlightRef.current;
    if (inFlight !== null) {
      await inFlight;
      return false;
    }
    const attempt = work();
    practiceInFlightRef.current = attempt;
    try {
      await attempt;
    } finally {
      // Cleared whether it resolved or threw, so a failed attempt can never wedge the slot shut.
      practiceInFlightRef.current = null;
    }
    return true;
  }, []);

  /** {@link restorePractice}, wrapped so its work occupies the shared slot. */
  const restorePracticeGuarded = useCallback(async (): Promise<void> => {
    await inPracticeSlot(restorePractice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorePractice, inPracticeSlot]);
  restorePracticeRef.current = restorePracticeGuarded;

  const runOpenPractice = useCallback(
    async (startOver: boolean): Promise<PracticeBeginOutcome> => {
      const seams = practiceSeamsRef.current!;
      // Resolved once per load and shared: a probe per activation would re-read OPFS and a quota
      // estimate every time the player tapped the CTA.
      capabilityRef.current ??= seams.capabilities.evaluate();
      const report = await capabilityRef.current;
      if (!report.supported) {
        return { kind: 'refused', report };
      }

      // The credential is minted **here**, on the player's act — not at boot. A player who never
      // taps the CTA never has a practice credential written.
      const record = seams.credentials.ensure();
      const outcome = await openPracticeSession({
        baseUrl: deps.baseUrl,
        fetchImpl: deps.fetchImpl,
        credential: record.credential,
        startOver,
      });
      if (!liveRef.current) {
        return { kind: 'unavailable', message: 'The screen went away.' };
      }
      if (outcome.kind === 'unsupported') {
        return { kind: 'unavailable', message: 'This server does not offer practice.' };
      }
      if (outcome.kind === 'unavailable') {
        return outcome;
      }
      if (outcome.kind === 'expired') {
        // Nothing is opened and nothing is created: the player is told their work expired, and
        // starting over is an explicit second act.
        setPracticeBoot(null);
        practiceBootRef.current = null;
        return { kind: 'expired', expiredAt: outcome.expiredAt, retentionDays: outcome.retentionDays };
      }
      if (outcome.kind === 'absent') {
        // Unreachable on this path — `absent` is the *resume-only* answer and this call does not set
        // that flag, so a server asked to provision either provisions or refuses. Handled rather than
        // cast away: a server that answered it anyway has declined to open a session, and inventing a
        // handle from a response that carries none is exactly the class of guess the typed outcomes
        // exist to prevent.
        return { kind: 'unavailable', message: 'The hive did not open a practice sandbox.' };
      }

      // **Starting over retires the previous session's coordinator, explicitly.**
      //
      // It cannot be inferred from the scope. `start_over` provisions a fresh sandbox — document
      // reset, history cleared — but the scope is `practice-{credential}` and the credential is not
      // reissued, so the *same* scope now names a different sandbox. A coordinator kept across that
      // holds the retired session's document and base ordinal, and the first save or Run submits
      // that stale text against the fresh sandbox: refused as `stale` with no way out short of a
      // reload, or silently committing the old document if the ordinals happen to line up.
      //
      // Both halves go: the ref, so the block below rebuilds rather than reusing, and `practiceColony`
      // — because that block is guarded on a colony being present, and leaving the previous one there
      // would rebuild the coordinator from the retired sandbox's read before the fresh one lands.
      //
      // A THIRD thing must retire here for exactly the same reason (review, P1-C): the chat
      // channel's uncertainty marker. Scoping its sessionStorage key to `outcome.session.scope`
      // closes the *round-transition* half of the stale-marker bug (a new round gets a new
      // tournament sandbox id), but this comment's own point — "it cannot be inferred from the
      // scope" — means scoping alone does NOT close the *practice start-over* half: the scope
      // string is `practice-{credential}` either way, unchanged by `start_over`, so an ambiguous
      // send's marker from the retired sandbox would otherwise still be sitting under the exact
      // key the fresh sandbox's channel is about to read from, and boot it straight into
      // `uncertain` for a submission that has nothing to do with it. There is no channel instance
      // to ask yet at this point (the fresh one is built from `boot` below), so this clears the
      // same scope-keyed store directly rather than routing through one.
      if (startOver) {
        practiceCoordinatorRef.current = null;
        setPracticeColony(null);
        createChatUncertaintyMarkerStore(windowSessionStorage(), `practice:${outcome.session.scope}`).clear();
      }

      const boot = openPracticeBoot(outcome.session);
      setPracticeBoot(boot);
      practiceBootRef.current = boot;
  
      // Seed the editor exactly as the tournament path does — from the sandbox's own colony read.
      const colony = await boot.client.fetchColony();
      if (liveRef.current && practiceBootRef.current === boot && colony !== null) {
        setPracticeColony(colony);
      }

      // **Created and resumed are one outcome from here on** (`home-and-single-player`). They used to
      // open two different sheets — a configuring one and a "Welcome back" one — and neither exists:
      // there is nothing to configure, and a resumed sandbox's run list is the script selector's. So
      // both simply mean "the sandbox is open", and the player is taken into it.
      //
      // **Reopening from Home is a reopen too** (`client-practice`: "the player later reopens the
      // practice sandbox"). The requirement is about the *sandbox* being reopened, not about how — a
      // player who was interrupted and comes back through Home's affordance is owed the same account
      // of the missing run as one who reloaded the route. Safe to call from both paths: the report is
      // retired by reclaiming the run's bytes, so whichever gets there first makes the other silent.
      if (outcome.kind === 'resumed') {
        await reportInterruptedRun(seams.traceStore, (await seams.traceStore?.listRuns()) ?? []);
      }
      return { kind: 'opened' };
    },
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * **The practice open, single-flighted** (`client-practice` "Two concurrent opens produce one
   * session").
   *
   * `runOpenPractice` is the work; this decides whether this caller does it or joins someone already
   * doing it. A joiner has no outcome of its own — the request it would have made was never sent — so
   * it reports the session that actually settled.
   *
   * **`startOver` is exempt and runs unconditionally.** It asks to *discard* the current sandbox and
   * provision a fresh one, so joining an in-flight resume and reporting its session would be the exact
   * opposite of what was asked: the player would press "Start over" and be handed the colony they were
   * trying to leave.
   */
  const openPractice = useCallback(
    async (startOver: boolean): Promise<PracticeBeginOutcome> => {
      if (startOver) {
        return runOpenPractice(true);
      }
      let outcome: PracticeBeginOutcome | null = null;
      const ran = await inPracticeSlot(async () => {
        outcome = await runOpenPractice(false);
      });
      if (ran && outcome !== null) {
        return outcome;
      }
      // Joined a restore or another open. Report whatever settled rather than re-asking the server.
      if (practiceBootRef.current === null || !liveRef.current) {
        return { kind: 'unavailable', message: 'That practice session could not be opened.' };
      }
      return { kind: 'opened' };
      // `deps` is a DI bundle injected once by the caller, not reactive props.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [runOpenPractice, inPracticeSlot],
  );

  /** Builds the practice session's client and its device-side Run seam. The one place either is
   * constructed; no screen constructs either. */
  const openPracticeBoot = useCallback(
    (session: PracticeSessionHandle): PracticeBoot => {
      // No `renewToken` here, deliberately: a practice membership is a device-minted credential in
      // its own store with its own resume path (`practice-resume.ts`), not a tournament credential,
      // so the tournament mint could not renew it. Wiring the practice mint into the same seam is a
      // separate change and no reported finding touches it.
      const client = new RoundsHttpClient({
        baseUrl: deps.baseUrl ?? '',
        scope: session.scope,
        token: session.token,
        fetchImpl: deps.fetchImpl,
      });
      const { createWorker, compile } = practiceSeamsRef.current!;
      // **Both wasm seams or neither.** A controller with a worker but no compiler would have to
      // either skip the on-device gate (sending the request `rounds-local-run` forbids) or assume
      // every document compiles — so the absence of one is the absence of the run path, and the
      // screen refuses a run with a stated reason rather than starting one it cannot gate.
      const run =
        createWorker === undefined || compile === undefined
          ? null
          : createPracticeRunController({
              transport: createPracticeRunTransport({
                baseUrl: deps.baseUrl,
                fetchImpl: deps.fetchImpl,
                scope: session.scope,
                token: session.token,
              }),
              compile,
              createWorker,
              onProgress: () => {
                // The device already knows how far it has written; this only asks the reducer to
                // re-read it, which is the local counterpart of the run-progress poll.
                practiceCoordinatorRef.current?.coordinator.dispatch({ kind: 'poll-requested' });
              },
              // **Watching the run while it writes** (task 5.13). The accumulation of the worker's
              // deltas into one growing view is `practice-watch.ts`'s; this only keeps one watcher per
              // run — replacing it when a new run's first view arrives, so a previous run's frames can
              // never be folded into the run the player is now watching — and hands the coordinator
              // the trace to draw. Nothing here is written anywhere: a reload leaves no watcher and no
              // world, which is what "not durable" means on this side of the boundary.
              onWatch: (runId, view) => {
                const target = practiceCoordinatorRef.current?.coordinator;
                if (target === undefined) {
                  return;
                }
                if (practiceWatcherRef.current?.runId !== runId) {
                  practiceWatcherRef.current = createPracticeWatcher(runId);
                }
                const watching = practiceWatcherRef.current.observe(view);
                if (watching !== null) {
                  target.dispatch({ kind: 'run-watched', runId, watching });
                }
              },
            });
      return { session, client, run };
    },
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * **Two entry points, and the CTA navigates.**
   *
   * `begin` used to only *open* a session; a second press on a configuring sheet is what entered the
   * sandbox. There is no sheet and no second press, so the navigation moves here — into the one place
   * that knows the open succeeded.
   *
   * **`/practice`, not `/colony`** (`client-app` "A reload returns the player to the surface they were
   * on"). The practice sandbox has its own path, so the address bar records which of the two
   * independent sessions the player is in — which is what makes reloading there recoverable at all.
   */
  const enterPractice = useCallback(
    async (startOver: boolean): Promise<PracticeBeginOutcome> => {
      const outcome = await openPractice(startOver);
      if (outcome.kind === 'opened' && liveRef.current) {
        navigateRef.current(ROUNDS_PRACTICE_ROUTE);
      }
      return outcome;
    },
    [openPractice],
  );

  const practiceEntry: HomePracticeEntry = useMemo(
    () => ({
      begin: () => enterPractice(false),
      startOver: () => enterPractice(true),
    }),
    [enterPractice],
  );

  // The Beekeeper channel (**F3**), memoized on the client for the same reason the two seams above
  // are: a channel rebuilt per render would restart the status watch and drop the cooldown
  // countdown it is holding.
  const prompts: RoundsPromptChannel | undefined = useMemo(
    () =>
      client === undefined
        ? undefined
        : createRoundsPromptChannel({
            transport: client,
            store: clientStorePromptSeam(),
            clock: deps.clock,
            scheduler: deps.scheduler,
          }),
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client],
  );

  // One status watch per channel, stopped when the root goes. The watch holds a long-poll open and
  // writes to the store when it lands, so an unstopped one would keep mutating state behind a tree
  // that no longer exists.
  useEffect(() => prompts?.start(), [prompts]);

  // The chat overlay's channel (beekeeper-agent-chat, CONTRACT.md §7): the client's `chat-sse`
  // transport over the same `RoundsHttpClient` (`createChatClient()` shares its baseUrl/scope/
  // token), PLUS `client.fetchColony` itself for "closing the loop" (a turn that cuts a version
  // re-reads the colony so the editor shows what the Beekeeper just changed — `chat-channel.ts`'s
  // own module note). Memoized on `client` for the same reason `colonyRun`/`prompts` are — a
  // channel rebuilt per render would drop whatever transcript state it was mid-accreting.
  const chat: ChatSeam | undefined = useMemo(() => {
    if (client === undefined) {
      return undefined;
    }
    // The concrete sandbox this client addresses — `boot.session.sandbox`, the same identifier
    // `RoundsHttpClient`/`ChatSseClient` scope their own requests to (review, P1-C). A coarse
    // `'tournament'` constant let a stale marker survive a round transition (a new sandbox scope
    // replacing the old one) and boot a fresh, empty thread straight into `uncertain` — the new
    // sandbox has never seen the old submission and never will, so nothing could ever resolve it.
    // `client !== undefined` implies `boot?.client` is non-null, which in turn implies
    // `boot.session.sandbox` is non-null (`openMembership` only builds a client when it is) — the
    // `?? null` here is defensive only, so a channel would still be produced (with no marker
    // persistence, degrading gracefully) if that invariant were ever violated.
    const sandboxScope = boot?.session.sandbox ?? null;
    const chatClient = client.createChatClient();
    return createChatChannel({
      transport: {
        sendMessage: (text, targetType, handlers) => chatClient.sendMessage(text, targetType, handlers),
        attachTurn: (turnId, afterCursor, handlers) => chatClient.attachTurn(turnId, afterCursor, handlers),
        stopTurn: (turnId) => chatClient.stopTurn(turnId),
        fetchThread: () => chatClient.fetchThread(),
        clearThread: () => chatClient.clearThread(),
        fetchColony: () => client.fetchColony(),
      },
      store: clientStorePromptSeam(),
      uncertaintyMarker:
        sandboxScope === null
          ? undefined
          : createChatUncertaintyMarkerStore(windowSessionStorage(), `tournament:${sandboxScope}`),
    });
    // `boot?.session.sandbox` is the concrete scope key (see comment above); tracked alongside
    // `client` since a round transition can, in principle, hand back the very same `client`
    // reference while the underlying sandbox scope has moved on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, boot?.session.sandbox]);

  // Rehydration (7.15/7.16) is now folded into `useTurnGateBridge` below, which calls
  // `chat.rehydrate()` itself — see that hook's own doc for why sharing this exact promise with
  // the bridge's hydration-readiness rule rules out a separate, sibling effect here.

  // --- the sandbox screen's coordinator ------------------------------------------------
  //
  // The port is the coordinator's whole dependency (design **T2**): six async methods over the one
  // client, and deliberately not `RoundsHttpClient` itself, so nothing in `ui/rounds/` knows what a
  // fetch is. `submitRun`/`awaitCompletion` go through `colonyRun` rather than the client directly
  // so the review rail keeps refreshing off a run's own completion — one place decides that, not
  // two.
  const colonyMeta = useClientStore((state) => state.colonyMeta);
  const port: SandboxPort | undefined = useMemo(() => {
    if (client === undefined || colonyRun === undefined) {
      return undefined;
    }
    const traceReader = client.createTraceReader();
    return {
      activateVersion: (ordinal) => client.activateVersion(ordinal),
      saveDocument: (source, baseVersion) => client.handEditDocument(source, baseVersion),
      submitRun: (candidate) => colonyRun.submit(candidate),
      awaitCompletion: (runId) => colonyRun.awaitCompletion(runId),
      // `rounds-http` exposes the run ledger as a list, not per-run, so one progress read is a
      // list read filtered here. `null` — never a fabricated zero-progress row — when the run is
      // not in the answer, which is what lets the reducer decline to move the track.
      fetchRun: async (runId) => {
        const rows = await client.listRuns();
        const row = rows.find((candidate) => candidate.runId === runId);
        return row === undefined
          ? null
          : {
              runId: row.runId,
              status: row.status,
              ticksObserved: row.ticksObserved,
              terminalTick: row.terminalTick,
            };
      },
      // Design D3: the locator's sandbox arm carries the numeric id already in hand — no
      // `String(runId)` conversion on this seam any more.
      openTrace: (runId) => traceReader.open({ kind: 'sandboxRun', runId }),
      // The tournament's shared preview trace (`rounds-preview-run`) — over the same reader,
      // addressed by nothing but the caller's own bearer.
      openPreview: () => traceReader.open({ kind: 'tournamentPreview' }),
    };
    // `colonyRun` is itself memoized on `client`, so this rebuilds exactly when the client does.
  }, [client, colonyRun]);

  // Built **once**, as soon as both the transport and the first roster exist, and never rebuilt:
  // the coordinator holds the screen's whole transactional state (epoch, selection, run, playhead),
  // so a rebuild would silently discard an in-flight selection or a running run. A ref rather than
  // `useMemo` because `useMemo` is explicitly allowed to forget its value.
  const coordinatorRef = useRef<SandboxCoordinator | null>(null);
  if (coordinatorRef.current === null && port !== undefined && colonyMeta !== null) {
    const document = documentSnapshotOf(colonyMeta);
    coordinatorRef.current = createSandboxCoordinator(port, {
      ...initialSandboxState(),
      document,
      turnInFlight: colonyMeta.turnInFlight === true,
    });
  }
  // The tournament chat channel's turn-gate bridge (`beekeeper-progress-indicator` §3) — see
  // `useTurnGateBridge`'s own doc. `coordinatorRef.current` is read fresh every render, so the
  // coordinator built just above on THIS render is what the hook's dependency array sees, not a
  // render behind.
  useTurnGateBridge(chat, coordinatorRef.current);
  // --- the practice session's port and coordinator ---------------------------------------
  //
  // **The whole of `client-app`'s request accounting lives in this port.** Three of its six methods
  // are the shipped sandbox routes against the practice scope (a practice sandbox is a sandbox), and
  // the other three are entirely local: the run goes through the device-side controller, progress is
  // the device's own count, and the trace comes out of the device's own store. So a practice run
  // issues **no batch-run request and no trace-read request** — not because anything suppresses them,
  // but because there is nowhere here for either to come from.
  const practicePort: SandboxPort | undefined = useMemo(() => {
    if (practiceBoot === null) {
      return undefined;
    }
    const boot = practiceBoot;
    const traceStore = practiceSeamsRef.current!.traceStore;
    return {
      activateVersion: (ordinal) => boot.client.activateVersion(ordinal),
      saveDocument: (source, baseVersion) => boot.client.handEditDocument(source, baseVersion),
      submitRun: (candidate) =>
        boot.run === null
          ? Promise.resolve({
              kind: 'refused' as const,
              refusal: {
                kind: 'unavailable' as const,
                message: 'Practice runs aren’t available in this build.',
              },
            })
          : boot.run.submit(candidate),
      awaitCompletion: (runId) =>
        boot.run === null
          ? Promise.resolve({ kind: 'failed' as const, runId, status: 'unavailable' })
          : boot.run.awaitCompletion(runId),
      // Local. Never a server read — which is also why the run-progress poll below costs nothing
      // here and can keep the written-extent track moving at no request cost at all.
      fetchRun: (runId) => Promise.resolve(boot.run?.progressFor(runId) ?? null),
      // Local. `open` resolves against the device's own published manifest, so the commit marker,
      // the checksums and the index-bounds validation are all still crossed (**D-D**).
      openTrace: (runId) =>
        traceStore === undefined
          ? Promise.resolve({
              kind: 'corrupt' as const,
              component: 'coarse' as const,
              message: 'This device can’t keep replays, so this run can’t be watched back.',
            })
          : traceStore.open(runId),
      // Practice has no tournament and no preview: the screen's `inPractice` framing gates the
      // `preview-requested` intent that would call this, so it is never reached in practice.
      openPreview: () =>
        Promise.resolve({
          kind: 'unavailable' as const,
          message: 'Practice sessions have no tournament preview.',
        }),
    };
  }, [practiceBoot]);

  // Built once per practice session and rebuilt when there is none — which is not the same as "when
  // the scope changes", and used to be written as if it were. `start_over` provisions a fresh sandbox
  // *on the same scope* (`practice-{credential}`, and the credential is not reissued), so a
  // scope-keyed rebuild never fired for the one act that most needs it. `openPractice` clears this
  // ref itself on a start-over; the scope stays on the record only as what the coordinator belongs to.
  const practiceCoordinatorRef = useRef<{ scope: string; coordinator: SandboxCoordinator } | null>(
    null,
  );
  if (
    practiceBoot !== null &&
    practicePort !== undefined &&
    practiceColony !== null &&
    practiceCoordinatorRef.current?.scope !== practiceBoot.session.scope
  ) {
    practiceCoordinatorRef.current = {
      scope: practiceBoot.session.scope,
      coordinator: createSandboxCoordinator(practicePort, {
        ...initialSandboxState(),
        // **The one place the seek rule's execution site is declared** (`client-sandbox-screen`,
        // design **D-D**). A practice run steps on this device, so this coordinator — and only
        // this one — owns the writer and may resolve a seek into a run's written extent while it
        // is still being written. The tournament coordinator above keeps the shipped `'server'`
        // default, where the same read would cross the commit marker (**D-121**).
        runExecution: 'device',
        document: documentSnapshotOf(practiceColony),
      }),
    };
  }

  /**
   * **The device's verdict on a saved draft** (task 5.12; `rounds-local-run`).
   *
   * A practice sandbox has no server-side compile at all, so the `compiles` a draft carries has to come
   * from this device — and the compile is asynchronous while the edit is not. The sequencing lives here
   * rather than in the screen because the screen owns no lifecycle
   * (`SandboxScreen.host.test.tsx`: "`.then`/`await` would mean the screen is holding a result across
   * time, which is the thing epochs exist to arbitrate"), and it is the **same** compiler that gates
   * Run — a second one could pass a document the run then refused.
   *
   * Two staleness guards, both cheap and both real: `source` travels with the verdict so the reducer can
   * discard one for text already typed past, and the coordinator identity is re-checked so a verdict
   * cannot land on the coordinator of a session the player has since started over.
   */
  const compilePracticeDraft = useCallback((source: string): void => {
    const compile = practiceSeamsRef.current?.compile;
    const target = practiceCoordinatorRef.current?.coordinator;
    if (compile === undefined || target === undefined) {
      return;
    }
    void Promise.resolve(compile(source)).then((outcome) => {
      if (!liveRef.current || practiceCoordinatorRef.current?.coordinator !== target) {
        return;
      }
      target.dispatch({
        kind: 'draft-compiled',
        source,
        compiles: outcome.kind === 'compiles',
        // The compiler's own words, which is what the blocked-Run popup counts and lists.
        // **`line: 0` means "no location", deliberately.** The device compiler locates every
        // diagnostic, but `PracticeCompileOutcome` carries `RunDiagnostic` — type name and message —
        // so the position does not survive that seam. 0 is not a 1-based line, so `typeAtLine`
        // answers `null` and `PlaybackBar` offers no "open the offending location" control, which is
        // the existing path for a diagnostic with no attributable position; a fabricated line 1 would
        // send the player somewhere the error is not. Widening the seam to carry the position is a
        // follow-on, recorded rather than done here.
        diagnostics:
          outcome.kind === 'compiles'
            ? []
            : outcome.diagnostics.map((d) => ({ message: d.message, line: 0, column: 0 })),
      });
    });
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The **active** coordinator: the practice session's while one is open, the tournament round's
   * otherwise.
   *
   * Substituting here rather than at each consumer is what keeps the three timers, the playback loop
   * and the detail-residency probe single-owner: they already follow "the coordinator", and practice
   * changes which one that is rather than adding a second set of them.
   */
  /**
   * **Which sandbox the player is looking at is decided by the route, not by which session happens to
   * be open** (`client-app` "A reload returns the player to the surface they were on").
   *
   * This used to be "practice wins whenever a practice session exists", which was workable only while
   * both sandboxes shared the one `/colony` path. They no longer do, and that is the fix: one path
   * naming two independent sessions — different credential, scope, token and client — is exactly what
   * forced a reload to guess which was meant, and the guess was unrecoverable for whichever it got
   * wrong. With `/colony` and `/practice` distinct, the address bar answers the question and the two
   * sessions can be open at once without either shadowing the other.
   */
  const showingPractice = pathname === ROUNDS_PRACTICE_ROUTE && practiceBoot !== null;
  const coordinator = showingPractice
    ? (practiceCoordinatorRef.current?.coordinator ?? null)
    : coordinatorRef.current;
  /** The client the display-history reads go through — the active session's, for the same reason. */
  const activeClient = showingPractice ? practiceBoot.client : client;

  // Unmount cancellation is the coordinator's own rule (**4.3**): results that land afterwards are
  // discarded by the reducer rather than by aborting anything, so the root's only obligation is to
  // say when the screen went.
  useEffect(() => {
    if (coordinator === null) {
      return;
    }
    return () => coordinator.dispatch({ kind: 'cancelled' });
  }, [coordinator]);

  // The run-progress poll. The cadence is the composition root's — this is the timer design
  // **T1**/**T2** keep out of the screen — and it runs only while a run is actually writing.
  //
  // **A device-executed run is pushed, not polled, and gets no timer at all.** The two are
  // alternatives rather than a belt and braces: `practicePort.fetchRun` answers from
  // `progressFor` — the worker's own reports, never a server read — so polling it on a clock only
  // re-reads a value that nothing but a push can have changed. The push already arrives on its own
  // cadence (`coalesceProgress`, ~10/s), and it is ordered correctly at the source: `practice-run.ts`
  // records `ticksObserved` *before* it calls back, so the poll a push triggers reads the new value
  // rather than the previous one. Running both simply added coordinator dispatches — each one a state
  // change, so each one a whole-screen re-render — to the path this change exists to quieten.
  //
  // Read off `runExecution` rather than off `practiceBoot`, even though the two coincide today: the
  // field is the coordinator's own authoritative answer to "where do this sandbox's runs execute",
  // set once at construction, so this cannot drift if the practice wiring changes shape.
  const scheduler = deps.scheduler;
  useEffect(() => {
    if (coordinator === null || coordinator.getState().runExecution === 'device') {
      return;
    }
    let timer: number | null = null;
    let stopped = false;
    const setTimer = (fn: () => void, ms: number): number =>
      scheduler === undefined ? (setTimeout(fn, ms) as unknown as number) : scheduler.setTimer(fn, ms);
    const clearTimer = (id: number): void => {
      if (scheduler === undefined) {
        clearTimeout(id);
      } else {
        scheduler.clearTimer(id);
      }
    };
    const arm = (): void => {
      if (stopped || timer !== null || coordinator.getState().run.kind !== 'in-flight') {
        return;
      }
      timer = setTimer(() => {
        timer = null;
        if (stopped) {
          return;
        }
        coordinator.dispatch({ kind: 'poll-requested' });
        arm();
      }, RUN_PROGRESS_POLL_MS);
    };
    const unsubscribe = coordinator.subscribe(() => arm());
    arm();
    return () => {
      stopped = true;
      unsubscribe();
      if (timer !== null) {
        clearTimer(timer);
      }
    };
  }, [coordinator, scheduler]);

  // Closing the Beekeeper's loop into the editor. `chat-channel.ts` re-reads `GET /colony` after a
  // turn cuts a version, so the store's roster is the one place that learns the document moved; the
  // coordinator hears about it as the turn settling, which is exactly what it is. Without this the
  // editor would keep showing the document from before the turn.
  //
  // **Compared against the coordinator's own document, never a shadow ref.** This used to hold the
  // last dispatched source in a `useRef` that starts `null`, so the *first* observation — the
  // document the coordinator was constructed with, at boot, with no turn anywhere near it — read
  // as a change and was dispatched as a turn. `turn-settled` is not a no-op: it bumps the sandbox
  // epoch and settles a version selection nothing selected, and a settled selection is exactly
  // what `preview-requested`'s "something else already claims the world" guard refuses on. Every
  // preview auto-load was therefore dropped inside the coordinator for the life of the sandbox
  // (`rounds-preview-run`, `client-sandbox-screen`) — no request ever left the client.
  //
  // The coordinator's own document is the honest comparison anyway: it is the state this effect
  // exists to keep in step with, and a ref beside it could only ever drift from it — after a hand
  // edit it did, and the player's own saved document came back as a Beekeeper turn.
  useEffect(() => {
    // Scoped to the tournament session: `colonyMeta` is the *tournament* sandbox's roster, so
    // dispatching from it into a practice coordinator would install another session's document.
    // The condition is now "am I looking at the tournament coordinator", not "does a practice
    // session exist anywhere" (WAG-91): the practice channel writes its own roster slot rather than
    // this one, so an open practice session no longer says anything about `colonyMeta` — while the
    // old `practiceBoot !== null` guard silently disabled this loop for the *tournament* sandbox
    // for as long as a practice session happened to be open beside it.
    if (coordinator === null || colonyMeta === null || showingPractice) {
      return;
    }
    const document = documentSnapshotOf(colonyMeta);
    if (document === null || document.source === coordinator.getState().document?.source) {
      return;
    }
    coordinator.dispatch({ kind: 'turn-settled', document });
  }, [coordinator, colonyMeta, showingPractice]);

  /**
   * **The same loop, closed for the practice sandbox** (WAG-91).
   *
   * A practice session's roster arrives on its own slot (`practiceColony`, written by the practice
   * chat channel's store seam above and by the boot/start-over reads), and its coordinator is a
   * different one, so this is a second effect rather than a branch inside the first: the two read
   * different state and dispatch into different coordinators, and merging them would only re-derive
   * which of the two is which.
   *
   * Without it a practice Beekeeper turn changed nothing a player could see. The editor kept the
   * document from before the turn; `headVersion` — the coordinator's own `document.version` — never
   * moved, so the version-history effect below (keyed on exactly that) never refetched; and the
   * version the transcript had just announced as `Set updated → vN` was in the sandbox's history but
   * in nothing the selector had read.
   *
   * Compared against the coordinator's own document rather than a shadow ref, for the reason the
   * tournament effect records: the coordinator is constructed *from* `practiceColony` at boot, so
   * the first comparison is an equality and dispatches nothing — a turn settling is the only thing
   * that can make the two differ.
   */
  useEffect(() => {
    const practiceCoordinator = practiceCoordinatorRef.current?.coordinator ?? null;
    if (practiceCoordinator === null || practiceColony === null) {
      return;
    }
    const document = documentSnapshotOf(practiceColony);
    if (document === null || document.source === practiceCoordinator.getState().document?.source) {
      return;
    }
    practiceCoordinator.dispatch({ kind: 'turn-settled', document });
  }, [practiceBoot, practiceColony]);

  // The root reads the coordinator's state for one reason only: to name the run the score pill
  // describes. The screen subscribes independently for everything else — two readers of one
  // reducer, no second copy of the state.
  const sandboxState = useSyncExternalStore(
    useCallback(
      (listener: () => void) => coordinator?.subscribe(listener) ?? (() => {}),
      [coordinator],
    ),
    () => coordinator?.getState() ?? null,
    () => coordinator?.getState() ?? null,
  );

  // --- the playback loop ----------------------------------------------------------------
  //
  // The second timer this root owns, and for the same reason as the first: a *cadence* is a
  // wall-clock decision, and the coordinator owns ordering rather than time (design **T2**). The
  // loop itself is `playhead-loop.ts`, injected with the frame scheduler and clock so it is
  // testable without a display; here it is handed the three facts it follows — the loaded trace,
  // whether the reducer says playback is on, and which speed step the bar is showing.
  //
  // **The speed step lives here**, not in the screen, because this is the only place that needs it:
  // the ladder's step is a multiplier on the loop's tick rate, and holding it beside the loop is
  // what stops the screen from owning a value it cannot act on. `SandboxScreen` takes it back as a
  // prop and reports changes up.
  const [speed, setSpeed] = useState<SandboxSpeed>(DEFAULT_SANDBOX_SPEED);

  // --- which steps the live watch window can sustain ------------------------------------
  //
  // A **device** run hands this screen only the first `DEFAULT_WATCH_WINDOW_TICKS` of world state;
  // past that the run keeps writing and keeps reporting progress, but nothing further crosses the
  // thread boundary. How long that lasts is a function of how fast the playhead eats it — 60 s at 1×,
  // and **3.75 s at 16×**, measured as the field emptying 3.8 s into a run and staying empty until
  // publication. So the fast steps are withheld while a run is in flight and restored when it lands.
  //
  // **`world.kind`, never `run.kind`.** The latter is `in-flight` for a *server* run too, which has no
  // watch window and no watched prefix at all; gating on it would restrict the tournament sandbox for
  // a limit that does not apply to it. The world arm is reachable only for a device run — the reducer
  // refuses `run-watched` outright for `runExecution: 'server'` — and `runExecution` is named beside
  // it so the intent is legible without knowing that rule.
  //
  // It follows that the ladder stays open for the ~2 s between pressing Run and the first watched
  // view arriving, and that is correct rather than a gap: during that window the world still holds
  // whatever it held before — a previously *published* trace, which is whole and sustains any step,
  // or nothing at all, in which case the loop does not run. Gating on `run.kind` instead would
  // restrict playback of a complete trace for a window that does not apply to it.
  const watchedTrace = sandboxState === null ? null : watchableTrace(sandboxState);
  const watchingInFlight =
    sandboxState?.world.kind === 'in-flight' && sandboxState.runExecution === 'device';
  const admissibleSpeeds = watchingInFlight
    ? admissibleSandboxSpeeds(
        DEFAULT_WATCH_WINDOW_TICKS,
        // The run's own rate, never a build constant: the window is a count of ticks, so how long it
        // lasts depends on the rate those ticks are played at.
        //
        // **`0` is the explicit "unknown" sentinel, not a shrug** — the same convention the screen
        // states at length (client-tick-rate-provenance / tick-rate-single-source **E4**): "no build
        // default in general … `0` is an explicit 'unknown' sentinel, never a plausible rate (the
        // validated floor is 2 Hz)". Substituting `SIM_TICK_RATE_HZ` here would read more clearly and
        // be exactly the thing that rule forbids — inventing a plausible rate for a trace that does
        // not exist. `admissibleSandboxSpeeds` rejects it and returns the default step alone, which
        // is the safe answer. Unreachable in practice regardless: the `in-flight` world arm always
        // carries a trace, so `watchingInFlight` implies `watchedTrace !== null`.
        watchedTrace?.tickRateHz ?? 0,
        MIN_WATCH_SECONDS,
      )
    : SANDBOX_SPEED_STEPS;
  // **The player's choice is untouched; what runs is derived from it.** Disabling a control does not
  // change the value behind it — a 16× chosen while replaying would otherwise still multiply the loop
  // once a run started, with the button merely greyed out. Deriving instead of clamping the stored
  // step is also what makes publication restore the preference with no effect and no extra state.
  const effectiveSpeed = effectiveSandboxSpeed(speed, admissibleSpeeds);

  usePlayheadLoop({
    coordinator,
    // **Either arm**, through the coordinator's own unifier — a watched run advances its playhead the
    // same way a published one does (that is what "the playhead may chase the write head" *is*), and a
    // loop that only followed `loaded` would leave the world frozen on the first tick it drew. How far
    // the playhead may go is still the reducer's, bounded by the write head.
    trace: watchedTrace,
    playing: sandboxState?.playback.playing ?? false,
    speed: effectiveSpeed,
    scheduler: deps.frameScheduler ?? rafFrameScheduler,
    clock: deps.clock ?? performanceClock,
  });

  // --- detail-window residency for the sandbox's world view -----------------------------
  //
  // A `ReviewTrace` opens with its coarse tier only; the detail records the world view actually
  // draws arrive one window at a time and must be **asked for**. Design **T1** puts every request
  // outside the screen (task 8.3 asserts `SandboxScreen.tsx` issues none), and the coordinator
  // owns transactions rather than frames — so the ask lives here, with the other timers.
  //
  // `requestDetail` is fire-and-forget and idempotent per window: it fills the trace's own
  // residency map and has no way to tell React the bytes landed. Hence the bounded probe, which
  // re-renders once the window is resident and gives up rather than spinning on a tick whose
  // records genuinely are not in the trace.
  //
  // **And it asks AHEAD of the playhead**, which is a defect fix rather than an optimisation.
  // Detail is fetched one ~1-sim-second window at a time, and asking only for the window the
  // playhead is already in meant that at 1x playback it crossed a window boundary every second and
  // *raced an HTTP round trip every time*. Each lost race dropped frames to the coarse tier, which
  // the player saw as the field strobing once a second: the bees vanishing and the wide tier's
  // orange swarm markers flashing up in their place. `detailPrefetchTicks` scales the lead with the
  // speed step, since speed is what sets how fast windows are consumed, and caps the burst so a
  // fast scrub cannot open unbounded fetches. (`SandboxScreen` holds the last real frame across the
  // gaps this cannot close — `ui/rounds/detail-continuity.ts`.)
  const [, setResidencyProbe] = useState(0);
  useEffect(() => {
    if (sandboxState === null || sandboxState.world.kind !== 'loaded') {
      return;
    }
    const trace = sandboxState.world.trace;
    const tick = sandboxState.playback.playheadTick;
    if (tick === null) {
      return;
    }
    for (const ahead of detailPrefetchTicks({
      tick,
      // The rate the playhead is *actually* advancing at, so the lead cannot be computed for a step
      // the loop is not running. The two coincide today — this effect returns above unless the world
      // is `loaded`, and the two diverge only while it is `in-flight` — so this is the invariant made
      // structural rather than left resting on two unrelated guards agreeing.
      speed: effectiveSpeed,
      tickRateHz: trace.tickRateHz,
      terminalTick: trace.terminalTick,
      playing: sandboxState.playback.playing,
    })) {
      trace.requestDetail(ahead);
    }
    if (trace.detailAt(tick) !== null) {
      return;
    }
    trace.requestDetail(tick);
    let cancelled = false;
    let attempts = 0;
    let timer = setTimeout(function probe() {
      if (cancelled) {
        return;
      }
      attempts += 1;
      if (trace.detailAt(tick) !== null) {
        setResidencyProbe((count) => count + 1);
        return;
      }
      if (attempts < DETAIL_RESIDENCY_PROBES) {
        timer = setTimeout(probe, DETAIL_RESIDENCY_PROBE_MS);
      }
    }, DETAIL_RESIDENCY_PROBE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sandboxState, speed]);

  // --- the version selector -------------------------------------------------------------
  //
  // The two reads the version surface added (`GET /versions`, and `listRuns` for the candidates
  // that attach to them) are fetched here rather than inside the coordinator, deliberately: they
  // are *display* history, not transactional state, so re-reading them can never disturb an
  // in-flight selection. The coordinator still owns what a selection *does* — this only supplies
  // the rows.
  //
  // Refreshed whenever something that cuts or moves a version settles: the head ordinal changing
  // (a save, an activation, or an agent turn), a run landing in the rail, or a turn ending. Each of
  // those is a real change to what the menu should show; nothing here polls.
  const [versionHistory, setVersionHistory] = useState<{
    headOrdinal: number;
    versions: readonly VersionSummary[];
  }>({ headOrdinal: 0, versions: [] });
  const [candidates, setCandidates] = useState<readonly MenuCandidate[]>([]);
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  /** Which order the selector's rows are in. Root state rather than the component's, because the
   * ordering is `buildVersionMenu`'s and a second copy inside the control could disagree with the list
   * on screen. */
  const [versionMenuSort, setVersionMenuSort] = useState<VersionMenuSort>('recent');

  // The G5 phase read is owned by the composition root, not either screen. Its `onReading`
  // callback is also the sole round-boundary detector, so the client never polls twice for the
  // same fact and never invents a transition from local time.
  const phase = useMemo(
    () =>
      boot === null
        ? undefined
        : createRoundsPhaseChannel({
            // The phase read needs no sandbox, so a spectator polls it too (**F3**).
            transport: boot.reads,
            clock: deps.clock,
            scheduler: deps.scheduler,
            onReading: (reading) => {
              /**
               * **A round only ever moves forward** (implementation review **F1**, the second
               * half).
               *
               * The phase channel now refuses to report a reading that belongs to a stopped run,
               * which is where a regressive report came from; this is the defence in depth the
               * finding asks for, and it is warranted because the cost of being wrong is a rebind
               * *backwards*: the previous round's sandbox scope reinstalled, the current round's
               * state cleared, and the round the tournament has already left reseeded over it.
               *
               * So anything that is not a strictly greater integer is not a boundary. Equal is the
               * ordinary case (no boundary), lower is stale or self-contradictory, and non-integral
               * is not a round at all.
               */
              const reported = reading.currentRound;
              if (!Number.isInteger(reported) || reported <= roundEpochRef.current.round) {
                return;
              }
              let reboundClient: RoundsHttpClient | null = null;
              roundEpochRef.current = rebindRound(
                roundEpochRef.current,
                reported,
                {
                  // Advancing the ref first is what makes every response issued under the old
                  // epoch stale before any new-round state is installed.
                  retire: () => {
                    coordinatorRef.current?.dispatch({ kind: 'cancelled' });
                  },
                  rebind: (round) => {
                    // A member with no sandbox has none to rebind, but the round they are watching
                    // still moved: the membership and the round-scoped sandbox client are separate
                    // facts and only the second is absent. `boot.reads` rides the spread, so the
                    // phase and standings surfaces are never rebuilt by a round boundary.
                    reboundClient =
                      reading.sandbox === null
                        ? null
                        : new RoundsHttpClient({
                            baseUrl: deps.baseUrl ?? '',
                            scope: reading.sandbox,
                            token: boot.session.token,
                            renewToken: sessionRenewal(
                              deps,
                              boot.session.credential,
                              boot.session.displayName,
                            ),
                            fetchImpl: deps.fetchImpl,
                          });
                    setBoot({
                      ...boot,
                      session: { ...boot.session, sandbox: reading.sandbox, round },
                      client: reboundClient,
                    });
                  },
                  reset: () => {
                    // The coordinator is the only thing to clear: the document a rebuilt one
                    // opens with IS the comparison the turn-report effect makes, so there is no
                    // second copy of it left behind to reset.
                    coordinatorRef.current = null;
                    useClientStore.getState().reset();
                  },
                  clear: () => {
                    setRuns([]);
                    // No `setReviewRound(null)` here any more: the round being reviewed is the
                    // route's, and a round rebind must not rewrite the address bar underneath a
                    // player who is deliberately looking at an earlier round. The viewer snapshot is
                    // still cleared — it is keyed by round and belongs to the retired one.
                    setReviewViewer(null);
                    setStandingsRead(null);
                    setVersionHistory({ headOrdinal: 0, versions: [] });
                    setCandidates([]);
                    setVersionMenuOpen(false);
                  },
                  reseed: (_round, epoch) => {
                    const nextClient = reboundClient;
                    if (nextClient === null) {
                      return;
                    }
                    void nextClient.fetchColony().then((colony) => {
                      if (roundEpochRef.current.epoch === epoch && colony !== null) {
                        useClientStore.getState().applyColonyMeta(colony);
                      }
                    });
                    void nextClient.listReviewRuns().then((history) => {
                      if (roundEpochRef.current.epoch === epoch) {
                        setRuns(history);
                      }
                    });
                  },
                },
              );
            },
          }),
    // `boot` changes only when membership or round scope changes; DI is stable by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, boot],
  );

  useEffect(() => phase?.start(), [phase]);
  const phaseSnapshot = useRoundsPhase(phase);
  // Standings need no sandbox either — "readable by every member" is the transport contract, and a
  // spectator is a member (**F3**).
  const reads = boot?.reads;
  const standingsSource = useMemo(
    () => (reads === undefined ? undefined : createStandingsSource({ transport: reads })),
    [reads],
  );
  useEffect(() => {
    if (standingsSource === undefined) {
      setStandingsRead(null);
      return;
    }
    let current = true;
    void standingsSource.read().then((read) => {
      if (current) setStandingsRead(read);
    });
    return () => {
      current = false;
    };
  }, [standingsSource, phaseSnapshot.round, phaseSnapshot.reportedPhase]);

  const colonyRoster = useMemo(() => {
    if (standingsRead === null || standingsRead.kind === 'failed') return null;
    return deriveColonyRoster({
      standings: standingsRead.standings,
      ownColonyId: phaseSnapshot.colonyId,
      ownJoinOrdinal: boot?.session.joinOrdinal ?? null,
    });
  }, [standingsRead, phaseSnapshot.colonyId, boot?.session.joinOrdinal]);

  const reviewResult: SandboxReviewResult | null = useMemo(() => {
    // `roundResultReadable`, not a second spelling of it: the sandbox's own waiting arm reads the
    // same predicate, so the card can never render with nothing to draw (E2E finding M-5 — the
    // standings phase, where a one-round tournament spends the battle-royale playback).
    if (!roundResultReadable(phaseSnapshot.surface) || phaseSnapshot.round === null || colonyRoster === null) {
      return null;
    }
    const own = colonyRoster.ownPlacementFor(phaseSnapshot.round);
    return own === null
      ? { round: phaseSnapshot.round, placement: null, score: null, shared: false, detailEvicted: false }
      : {
          round: phaseSnapshot.round,
          placement: own.placement,
          score: own.score,
          shared: own.shared,
          detailEvicted: false,
        };
  }, [phaseSnapshot.surface, phaseSnapshot.round, colonyRoster]);

  /**
   * The ranked roster for a given round.
   *
   * A cached function rather than a `useMemo`, for the reason `reviewSubjectFor` is one: the round
   * being reviewed now arrives from the sub-router, which renders below this component, so it is not
   * knowable at hook-call time. The cache is keyed on exactly what the memo's dependency array held,
   * so identical inputs still yield the identical array — which matters because this is handed to the
   * review screen's colony rail as a prop.
   */
  const gradedColoniesCacheRef = useRef<{
    round: number | null;
    roster: typeof colonyRoster;
    value: readonly GradedColony[];
  } | null>(null);
  const gradedColoniesFor = (round: number | null): readonly GradedColony[] => {
    const cached = gradedColoniesCacheRef.current;
    if (cached !== null && cached.round === round && cached.roster === colonyRoster) {
      return cached.value;
    }
    const value: readonly GradedColony[] =
      round === null || colonyRoster === null
        ? []
        : colonyRoster
            .placementsFor(round)
            .filter(
              (row): row is typeof row & { score: number; placement: number } =>
                row.score !== null && row.placement !== null,
            )
            .map((row) => ({
              colony: row.colony,
              displayName: row.displayName,
              score: row.score,
              placement: row.placement,
            }));
    gradedColoniesCacheRef.current = { round, roster: colonyRoster, value };
    return value;
  };

  // The coordinator's own head, **or `null` when it does not have one** — and the distinction has
  // to be drawn here rather than left to `??` at the three call sites below.
  //
  // `documentSnapshotOf` stores `0` for "no known base" (see its own comment: `GET /colony`'s
  // `documentVersion` is read opportunistically and the shipped server does not send it, so a
  // freshly-joined sandbox that has had no hand edit and no agent turn holds exactly this). `0` is
  // deliberately an ordinal no sandbox issues — but `??` only coalesces `null`/`undefined`, so
  // `0 ?? versionHistory.headOrdinal` is `0`, and the sentinel won every fallback it was supposed
  // to lose. Two things broke together, both from this one expression:
  //
  //   * a Run press submitted `baseOrdinal: 0` against a real head of `1`, so the server's
  //     base-ordinal CAS refused it `stale` — every first run in a fresh sandbox, refused with
  //     "This document moved on since you last edited it";
  //   * the version menu was built with `headOrdinal: 0` and rendered "No versions" while holding
  //     the one version it had just fetched.
  //
  // Normalising to `null` at the source keeps `??` the right operator at each call site and cannot
  // be forgotten by a fourth consumer later.
  const coordinatorHead = sandboxState?.document?.version ?? null;
  const headVersion = coordinatorHead !== null && coordinatorHead > 0 ? coordinatorHead : null;
  const turnInFlight = sandboxState?.turnInFlight ?? false;
  /** The run that just landed, or `null`. Changes exactly once per run — the reducer already holds
   * the identity, so no counter is needed beside it. */
  const settledRunId = sandboxState?.run.kind === 'settled' ? sandboxState.run.runId : null;
  useEffect(() => {
    if (activeClient === undefined) {
      return;
    }
    let live = true;
    // **A practice sandbox's candidates are the device's** (`practice-resume.ts`).
    // `GET /rounds/{scope}/runs` resolves its path through the tournament scope parser, so it is a
    // `404` for every practice scope and the list is empty there by construction — which read as
    // "not run" over the very version whose trace this device was holding.
    const localRuns = !showingPractice ? undefined : practiceSeamsRef.current?.traceStore;
    void Promise.all([
      activeClient.listVersions(),
      localRuns === undefined
        ? activeClient.listRuns().then((rows) =>
            rows.map((row) => ({
              runId: row.runId,
              documentVersion: row.documentVersion,
              status: row.status,
              score: row.score,
            })),
          )
        : localRuns.listRuns().then(practiceMenuCandidates),
    ]).then(([listed, rows]) => {
      if (!live) {
        return;
      }
      setVersionHistory({ headOrdinal: listed.headOrdinal, versions: listed.versions });
      setCandidates(rows);
    });
    return () => {
      live = false;
    };
    // `turnInFlight` deliberately dropped (`beekeeper-progress-indicator` §3): it is a proxy for
    // "a version may have been cut", which `headVersion` already covers, and leaving it in fired
    // this effect's `listVersions`/`listRuns` twice per turn (once on `turn-started`, once on
    // `turn-settled`) now that the bridge below actually dispatches those events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient, practiceBoot, headVersion, runs.length]);

  /**
   * **A finished practice run appears in the menu** — the refresh the effect above structurally
   * cannot do.
   *
   * That effect is keyed on the things that *cut or move a version*, and a run completing is not one
   * of them: a Run on an unedited document binds to the existing head, so `headVersion` never moves,
   * and `runs.length` is the tournament review rail, which a practice sandbox never populates. The
   * device's index gains the run's row at its terminal tick and nothing re-read it — so the version
   * the player had just simulated went on reading "not run", with the run itself nowhere on the menu
   * at all until some later, unrelated edit happened to move the head.
   *
   * Separate rather than another dependency on the effect above, because that one issues
   * `GET /versions` and this must issue **nothing**: `client-app`'s "a practice run issues no
   * simulation or trace-read request to the server" is accounted request-by-request, and a run's
   * conversation is exactly its two calls. A run changes the *runs*, not the versions, and on this
   * path the runs are a local read — so the honest refresh is free.
   *
   * The store write strictly precedes the settle (the device driver publishes at its commit point
   * and only then resolves the run), so this can never read the index one row short.
   *
   * Practice only, deliberately. The tournament path reaches its runs over the wire, where the same
   * refresh would put a request inside a run's accounted conversation; its menu still waits for the
   * next head move. Recorded here rather than fixed blind — it wants its own accounting decision.
   */
  useEffect(() => {
    const store = !showingPractice ? undefined : practiceSeamsRef.current?.traceStore;
    if (store === undefined || settledRunId === null) {
      return;
    }
    let live = true;
    void store.listRuns().then((rows) => {
      if (live) {
        setCandidates(practiceMenuCandidates(rows));
      }
    });
    return () => {
      live = false;
    };
  }, [practiceBoot, settledRunId]);

  // The draft row exists only while the editor holds uncommitted edits, and carries its error
  // count so a non-compiling draft is legible without pressing anything (**D12**).
  const draftRow = sandboxState?.draft ?? null;
  // **The version menu's in-flight override** (`version-menu-model.ts`'s `headRunInFlight`). The
  // epoch check is what scopes this to the run belonging to the *current* document: `select-version`
  // bumps `state.epoch` without cancelling a run already in flight for the version the player has
  // since navigated away from, and that abandoned run must not paint the newly-selected version's
  // trigger as running.
  const headRunInFlight =
    sandboxState?.run.kind === 'in-flight' && sandboxState.run.epoch === sandboxState.epoch;
  // **What the playhead is actually holding** — the tournament's shared preview, or one of the
  // player's own runs. Read here for the same reason `headRunInFlight` is: the menu's model is
  // pure and knows nothing of a world slot.
  const previewLoaded =
    sandboxState?.world.kind === 'loaded' && sandboxState.world.isPreview === true;
  /**
   * **The whole history — every version the sandbox has recorded** (WAG-91).
   *
   * This used to be a *shortlist*: `versionHistory.versions` filtered to the versions a run named,
   * plus the head as a standing exception. The rule came from `home-and-single-player` story 14 ("show
   * only versions that compiled and actually raced ... a shortlist of contenders rather than my
   * keystroke history"), and WAG-91 reverses it at the player's own request — "the scripts should be
   * scrollable and just show the versions in there". The reversal is recorded rather than the old
   * paragraph deleted, because the argument for the shortlist was real and this is a product decision
   * against it, not a discovery that it was wrong: the same story's neighbour (13) asks for every
   * version to be kept "so that I can roll back to anything", and a list that hides them is a
   * rollback the player cannot reach.
   *
   * It also removed the surface the Beekeeper writes to. An agent turn cuts a version and does not run
   * it, so **every script the Beekeeper wrote was filtered out** the moment the head moved past it —
   * the transcript said "Set updated → v4" and the selector had no v4 in it, which is the defect this
   * issue opens with. The head exception hid how total that was: it left exactly one agent version
   * visible, the current one, and only for as long as it stayed current.
   *
   * What the filter's absence costs is length, and length is now handled where it belongs — the panel
   * scrolls and is bounded by the world view (`versionSelectorListStyle`) instead of the list being
   * trimmed to fit a slab. Ordering still answers "continue" or "find my best" through the sort
   * toggle, which is the affordance story 17 actually asks for.
   */
  const activeOrdinal = headVersion ?? versionHistory.headOrdinal;
  const versionMenuModel = useMemo(
    () =>
      buildVersionMenu({
        // The coordinator's own head wins over the last fetched one: it is updated the moment an
        // activation or a save settles, so the trigger never lags a selection the player just made.
        headOrdinal: activeOrdinal,
        versions: versionHistory.versions,
        runs: candidates,
        draft: draftRow === null ? null : { errorCount: draftRow.diagnostics.length },
        headRunInFlight,
        previewLoaded,
        sort: versionMenuSort,
      }),
    [
      activeOrdinal,
      versionHistory.versions,
      candidates,
      draftRow,
      headRunInFlight,
      previewLoaded,
      versionMenuSort,
    ],
  );

  // Selecting a row is the compound **activate + load** (**D5**): one dispatch, so the document and
  // the trace can never be paired by two separate decisions. The coordinator stamps it with an
  // epoch and discards anything superseded — which is why this handler sequences nothing itself.
  const versionSelector =
    coordinator === null ? undefined : (
      <VersionMenu
        menu={versionMenuModel}
        open={versionMenuOpen}
        onOpenChange={setVersionMenuOpen}
        onSelectRow={(row) => {
          setVersionMenuOpen(false);
          if (row.kind === 'draft') {
            return;
          }
          coordinator.dispatch({
            kind: 'select-version',
            ordinal: row.ordinal,
            // `runs` is newest-first, so `[0]` is the row's most recent run — the one the
            // version-first presentation shows. A version never simulated has none, and the
            // selection then activates the document against an empty world rather than a stale one.
            runId: row.runs[0]?.runId ?? null,
          });
        }}
        onSelectRun={(run, row) => {
          setVersionMenuOpen(false);
          coordinator.dispatch({
            kind: 'select-version',
            ordinal: row !== null && row.kind === 'version' ? row.ordinal : (headVersion ?? 0),
            runId: run.runId,
          });
        }}
        turnInFlight={turnInFlight}
        sort={versionMenuSort}
        onSortChange={setVersionMenuSort}
        // Visual spec §8.1: the list sits **flush on the control stack's top edge**, spanning the
        // frame's full width inside a 12px gutter — not anchored to the trigger, which is a narrow
        // flexing cell and would truncate every summary. `fixed` because the stack is itself fixed
        // to the viewport bottom, so this needs no measurement of anything (the geometry-
        // independence rule the popups follow).
        //
        // **The stack's top edge, not the resting constants** (WAG-91). This was
        // `PLAYBACK_BAR_HEIGHT + BEE_TYPE_BAR_HEIGHT + PROMPT_BAR_HEIGHT` written out here — the
        // stack at rest, and 50px short of it whenever the Beekeeper's re-entry row is showing, which
        // put the panel's own bottom edge over the trigger that opens and closes it. The geometry now
        // lives in `sandbox-layout.ts` beside the arithmetic it depends on, and reads the height the
        // screen publishes; it also bounds and scrolls the panel, so a long history stays reachable.
        listStyle={versionSelectorListStyle()}
      />
    );

  // The run the counters and the score readout describe: the one the world view is showing, found
  // in the rail's own records rather than re-derived. `null` — two counter pills and no score pill
  // — whenever the screen is not on a scored run, which is the honest reading of "no score".
  const shownRunId = sandboxState?.world.kind === 'loaded' ? sandboxState.world.runId : null;
  const shownIndex = runs.findIndex((entry) => entry.runId === String(shownRunId));
  const shownRun = shownIndex === -1 ? null : runs[shownIndex]!;
  const previousScore = shownIndex > 0 ? runs[shownIndex - 1]!.score : null;

  /**
   * The caller's own colony for the round — **validated, and absent when there is none**
   * (implementation review **F4**).
   *
   * This used to be `phaseSnapshot.colonyId ?? 0`, and `0` is the defect: it is a real colony, so it
   * cannot mark absence. Because a tournament credential may legitimately read *other* members'
   * graded traces, authorization does not stop a substituted `0` either — the request succeeds and
   * a member whose phase payload carries no colony id (or a spectator, who has none at all) is
   * shown **colony 0's trace as their own subject**, addressed at `…/colonies/0/trace/…` and
   * labelled with their own name.
   */
  const ownColonyId = validColonyId(phaseSnapshot.colonyId);

  /**
   * **The review subject, built for the round the route names — and still referentially stable**
   * (D-208).
   *
   * `RoundsReview` opens the trace from an effect whose dependency is `subject.locator` — an
   * *object identity* — for the same reason it watches `reader`'s identity (see the memo comment
   * above `colonyRun`): an address that changed is a different trace to open. A fresh literal on
   * every render therefore reads as a fresh address on every render, and re-issues
   * `GET …/trace/manifest` (plus the coarse tier) each time.
   *
   * This was a `useMemo` on `[round, colony, roundsTotal]`. It cannot be one any more: the round now
   * arrives from the **sub-router**, which renders below this component, so it is not knowable at
   * hook-call time. A cache keyed on exactly the same three scalars preserves the property the memo
   * was there for — identical scalars yield the identical object — without needing the round in a
   * dependency array. One entry is enough because one round is displayed at a time.
   *
   * **F4:** with no validated colony there is no address to build, so this yields the typed
   * `gradedUnavailable` variant rather than a locator over a sentinel — structurally absent, so the
   * invalid state cannot be constructed.
   */
  const reviewSubjectCacheRef = useRef<{
    round: number | null;
    colony: number | null;
    roundsTotal: number | null;
    value: ReviewSubject | undefined;
  } | null>(null);
  const reviewSubjectFor = (round: number | null): ReviewSubject | undefined => {
    const cached = reviewSubjectCacheRef.current;
    if (
      cached !== null &&
      cached.round === round &&
      cached.colony === ownColonyId &&
      cached.roundsTotal === phaseSnapshot.roundsTotal
    ) {
      return cached.value;
    }
    const value: ReviewSubject | undefined =
      round === null
        ? undefined
        : gradedSubjectFor(round, ownColonyId, phaseSnapshot.roundsTotal);
    reviewSubjectCacheRef.current = {
      round,
      colony: ownColonyId,
      roundsTotal: phaseSnapshot.roundsTotal,
      value,
    };
    return value;
  };

  /**
   * **The viewer-state sink is stable, and value-equal snapshots are dropped** (D-208).
   *
   * `RoundsReview` reports its private playback state from an effect that lists this callback among
   * its dependencies, so an unstable identity makes the effect fire on every render — and, since
   * the effect's whole body is to call this, every render would store a new snapshot and schedule
   * another render. Stabilising the identity is what makes the report event-driven again.
   *
   * The equality check is the second half of the same guarantee rather than an optimisation: the
   * stored value is an object, so re-storing a value-identical snapshot would still change
   * `viewerState`'s identity downstream and re-run the effect that applies it. Dropping the no-op
   * keeps "nothing moved" from presenting as a change.
   */
  /**
   * The round the viewer-state sink is currently reporting for.
   *
   * A ref rather than a value in the callback's dependency array, for the reason the subject cache is
   * one: the round is the sub-router's and is not knowable when this hook is called. Written by
   * `renderGradedReview` immediately before the screen that will call this is rendered, so the sink
   * always attributes a report to the round the screen was rendered for.
   */
  const viewerRoundRef = useRef<number | null>(null);

  const onReviewViewerStateChange = useCallback((state: GradedReviewViewerState) => {
    const round = viewerRoundRef.current;
    if (round === null) {
      return;
    }
    setReviewViewer((current) =>
      current !== null &&
      current.round === round &&
      current.state.tick === state.tick &&
      current.state.zoom === state.zoom &&
      current.state.playing === state.playing &&
      current.state.subjectColony === state.subjectColony
        ? current
        : { round, state },
    );
  }, []);

  /**
   * **Builds the graded-review screen for the round the address bar names** (`client-rounds-review`
   * "The reviewed round is carried by the route, and is untrusted input").
   *
   * The round used to be React state (`reviewRound`) with `?? phaseSnapshot.round` behind it, and
   * that fallback was the defect: a reload lost the state and silently substituted the **currently
   * live** round, so a player reviewing round 2 during round 3 was shown round 3's trace under the
   * name of the round they had asked for. The address bar is now the only record of which round is
   * being reviewed, which is also why no second copy of it is persisted — a stored marker and the URL
   * can disagree after a history traversal, a bookmark or a shared link, and a route cannot disagree
   * with itself.
   *
   * The segment is player-supplied input on a path that addresses another member's bytes, so it is
   * resolved before anything can build a locator from it. Each refusal renders a *different* thing,
   * and the distinctions are the point:
   *
   *   * `total-unknown` — **pending**, not absent. Nothing is wrong with the round; the tournament's
   *     length has not landed yet, so it cannot be validated. Failing closed here is what stops
   *     `/review/999` addressing a trace during exactly the window in which the client cannot tell
   *     that 999 is not a round.
   *   * `malformed` / `out-of-range` — the typed absent state, with **no locator built and no
   *     `roundsTotal` handed down**, so an out-of-range round can never be rendered in a
   *     "round N of M" readout beside the tournament's real total.
   */
  const renderGradedReview = (rawRound: string | undefined): ReactElement => {
    const resolution = resolveReviewRound(rawRound, phaseSnapshot.roundsTotal);
    if (resolution.kind === 'unavailable') {
      viewerRoundRef.current = null;
      return (
        <RoundsReview
          // No `subject`, so no locator and no trace read is issued at all. `onOpenStandings` is
          // supplied even here: the review route must offer a way out in **every** state, and its
          // pending and empty states are exactly the ones reached with no seams (`client-rounds-review`
          // "The review route offers a way out before its seams have arrived").
          unavailableRound={{ reason: resolution.reason, raw: rawRound ?? '' }}
          onOpenStandings={() => navigateRef.current(STANDINGS_ROUTE)}
          createRenderer={deps.createRenderer}
        />
      );
    }
    const round = resolution.round;
    viewerRoundRef.current = round;
    const subject = reviewSubjectFor(round);
    return (
      <RoundsReview
        // **F4:** with no graded subject there is nothing for this route to open, and a run list
        // would let the review screen fall back to opening one of the caller's own *sandbox* runs
        // in a graded round's place. The route's own named unavailable state is the answer.
        runs={subject?.mode === 'gradedUnavailable' ? [] : runs}
        reader={reader}
        subject={subject}
        phase={phaseSnapshot}
        colonies={gradedColoniesFor(round)}
        viewerState={reviewViewer?.round === round ? reviewViewer.state : undefined}
        onViewerStateChange={onReviewViewerStateChange}
        onOpenStandings={() => navigateRef.current(STANDINGS_ROUTE)}
        createRenderer={deps.createRenderer}
        // Decided here rather than in the screen, because it needs the tournament's phase and the
        // screen holds no tournament model of its own.
        notGradedYet={roundIsNotGradedYet(round, phaseSnapshot)}
      />
    );
  };

  /**
   * The leaderboard's one read. Memoized so the screen's mount effect fires once rather than on every
   * render, in the same shape `standingsSource` is given to `Standings`: the screen owns *when* it
   * reads, the root owns *what* reading means.
   *
   * It presents **no host bearer and no query parameter** — the route is gated by whatever gates the
   * rest of the API, which is `deps.fetchImpl`'s business (the boot wraps it in the tenant token where
   * one is required) and not this call's.
   */
  const leaderboardRead = useMemo(
    () => () => fetchLeaderboard({ baseUrl: deps.baseUrl, fetchImpl: deps.fetchImpl }),
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // --- the host flow: **relocated to `/demo`** ---------------------------------------------
  //
  // This root used to compose the presenter's three host surfaces itself — gathering, leaderboard and
  // spectate — reached from Home's "Resume hosting" card. `home-and-single-player` removes that card,
  // and the composition goes with it rather than being left as an unreachable second mount: the
  // `/demo` composition root already builds all three from the same shipped components
  // (`DemoTournamentChapters.tsx`), already holds the host membership credential, and already owns
  // the create-and-host bootstrap and `recoverHostCredential`. One presenter surface, in one place.
  //
  // Nothing was deleted from the *stack*: `RoundsHostHttpClient`, `HostGathering`, `HostLeaderboard`,
  // `SpectateView`, the host credential and join-code stores and `recoverHostCredential` all still
  // compile and are all still tested, through `/demo` and their own component tests.

  // --- the practice session's screen chrome ----------------------------------------------
  //
  // **The framing states the fixed run configuration, not a chosen one.** It used to read a rival count
  // and a seed out of a ref, because both were the player's to pick; both are now facts about every run
  // in the game — three defensive rivals, and the operator's seed — so the pill states the shape of the
  // ladder rather than this session's settings. The seed itself is deliberately absent: nothing on this
  // side of the wire holds it until a run starts, and printing a placeholder was the defect this
  // replaces.
  const practiceFraming: SandboxPracticeFraming | null = !showingPractice
    ? null
    : { label: 'Practice', detail: 'three defensive rivals · ranked seed' };

  /**
   * The prompt is never locked. It used to be, in practice sessions, until the player supplied the
   * operator practice code (**D-G**); that code is gone and the Beekeeper is metered by the server's
   * own spend and concurrency ceilings instead, which refuse a turn rather than pre-emptively
   * disabling the prompt. `SandboxPromptLock` stays in the screen's contract because the *tournament*
   * path's own gating still uses it.
   */
  const promptLock: SandboxPromptLock | null = null;

  // The practice session's chat channel, over its own client — the same construction the tournament
  // path uses, against the practice scope. Built here because no screen constructs a channel.
  const practiceChat: ChatSeam | undefined = useMemo(() => {
    if (practiceBoot === null) {
      return undefined;
    }
    const chatClient = practiceBoot.client.createChatClient();
    const sandboxClient = practiceBoot.client;
    const boot = practiceBoot;
    return createChatChannel({
      transport: {
        sendMessage: (text, targetType, handlers) => chatClient.sendMessage(text, targetType, handlers),
        attachTurn: (turnId, afterCursor, handlers) => chatClient.attachTurn(turnId, afterCursor, handlers),
        stopTurn: (turnId) => chatClient.stopTurn(turnId),
        fetchThread: () => chatClient.fetchThread(),
        clearThread: () => chatClient.clearThread(),
        fetchColony: () => sandboxClient.fetchColony(),
      },
      /**
       * **The practice session's own roster slot, not the tournament store's** (WAG-91).
       *
       * `chat-channel.ts` re-reads `GET /colony` when a turn cuts a version and hands the result to
       * this seam — that read is the only thing that ever learns a Beekeeper turn moved the
       * document. This channel used to hand it to `clientStorePromptSeam()`, which writes the
       * *tournament* `colonyMeta` slice, so a practice turn's roster landed in another session's
       * state; the effect that closes the loop into the editor then had to refuse every practice
       * session outright to avoid installing it. The two together meant a practice sandbox never
       * learned anything: the editor kept the pre-turn document, the head ordinal never moved, and
       * the version list — refetched only when the head moves — never fetched the version the
       * transcript had just announced as `Set updated → vN`. It was simply not in the selector.
       *
       * Guarded on the boot identity for the same reason every other practice landing is: a colony
       * read answered after `start over` provisioned a fresh sandbox belongs to the sandbox that is
       * gone.
       */
      store: {
        applyColonyMeta: (meta) => {
          if (practiceBootRef.current === boot) {
            setPracticeColony(meta);
          }
        },
      },
      // Keyed to the concrete practice scope (review, P1-C) — `practiceBoot.session.scope`, the
      // same identifier `RoundsHttpClient`/`ChatSseClient` already address their own requests to —
      // rather than the coarse `'practice'` constant a prior revision used. `start_over` provisions
      // a fresh sandbox on a NEW scope (a fresh device credential is not minted, but the server-side
      // scope this boot names does change), so a coarse key would let a stale marker from the
      // abandoned sandbox survive into the new one and boot it straight into `uncertain`. The
      // `practice:` prefix keeps this namespace disjoint from the tournament channel's own key
      // even though the two scope formats do not otherwise collide.
      uncertaintyMarker: createChatUncertaintyMarkerStore(
        windowSessionStorage(),
        `practice:${practiceBoot.session.scope}`,
      ),
    });
  }, [practiceBoot]);

  // The practice chat channel's own turn-gate bridge (`beekeeper-progress-indicator` §3) —
  // independent of the tournament one above, over the practice coordinator, which the practice
  // route's own reload previously restored the thread for but never the gate (review F3): only
  // the tournament channel called `rehydrate()` at all, and the practice coordinator's
  // construction below omits a `turnInFlight` seed — so a reload mid-practice-turn lost both. This
  // hook now supplies practice's own `rehydrate()` call, mirroring the tournament one above.
  useTurnGateBridge(practiceChat, practiceCoordinatorRef.current?.coordinator ?? null);

  /**
   * **The sandbox screen, built once and mounted by whichever of the two sandbox routes matched.**
   *
   * One element for two paths, and no fork inside it: every prop that differs between a tournament
   * sandbox and a practice one already reads `showingPractice`, which is itself derived from the
   * route. So `/colony` renders this against the tournament session and `/practice` renders it against
   * the practice one, and only ever one of them is mounted at a time.
   *
   * Two screens would have been the obvious alternative and is the wrong one: they would share the
   * coordinator, the playback loop, the version selector and the chat channel, so the duplication
   * would be of the wiring rather than of anything a player can see.
   */
  const sandboxScreenElement =
    coordinator === null ? (
      // Never `undefined` at `/colony`: an absent `sandboxScreen` makes `AppRoutes` fall back to the
      // live Colony screen, and rounds mode mounting the colony screen — even for the few frames
      // before the roster lands — is the exact thing `client-colony` "In rounds mode this screen is
      // not mounted" forbids.
      //
      // Rendered visible rather than empty (it used to be `<div data-testid="sandbox-pending" />`
      // with nothing in it) — an empty div is indistinguishable from a stuck blank page, which is
      // exactly what a player reloading a sandbox path with a slow-to-settle restore saw. The
      // `NOTICE_STYLE` mould is the boot notice's own.
      <div data-testid="sandbox-pending" role="status" style={NOTICE_STYLE}>
        Waking the hive…
      </div>
    ) : (
      <SandboxScreen
        coordinator={coordinator}
        // **The mode fork, as one prop each.** A practice session gets no phase seam (there is
        // no phase), no standings route (there are no standings) and no selection seam (a
        // practice run mints nothing selectable — `rounds-practice-state`), and it gets the
        // practice framing for the slot a tournament's clock would occupy.
        practice={practiceFraming}
        promptLock={promptLock}
        phase={!showingPractice ? phase : undefined}
        onOpenStandings={
          !showingPractice ? () => navigateRef.current(STANDINGS_ROUTE) : undefined
        }
        reviewResult={!showingPractice ? reviewResult : null}
        onWatchGradedRound={(round) => {
          setReviewViewer(null);
          navigateRef.current(roundsReviewPath(round));
        }}
        dismissedDefaultingRounds={dismissedDefaultingRounds}
        onDismissDefaultingRound={(round) =>
          setDismissedDefaultingRounds((current) => new Set(current).add(round))
        }
        selectionCandidates={
          !showingPractice
            ? candidates.map((candidate) => ({
                candidateId: candidate.runId,
                // The version and score `candidates` already carries — never invented, and
                // `null` where the fact itself is (an unattributed run, a run with no score
                // yet), which composes down to no label at all rather than a fabricated one.
                label: candidateSelectionLabel(candidate),
              }))
            : []
        }
        markSelection={
          client === undefined || showingPractice
            ? undefined
            : (candidateId) => client.markSelection(candidateId)
        }
        types={typeViewsOf(!showingPractice ? colonyMeta : practiceColony)}
        run={shownRun}
        previousScore={previousScore}
        chat={!showingPractice ? chat : practiceChat}
        versionSelector={versionSelector}
        createRenderer={deps.createRenderer}
        buildCandidate={(source) =>
          candidateFromDocument(
            source,
            headVersion ?? versionHistory.headOrdinal,
            !showingPractice ? colonyMeta : practiceColony,
          )
        }
        // The step the loop is running, not the one the player picked: a ladder highlighting 16×
        // while the playhead advances at 1× is a lie about what is being watched. The pick survives
        // in `speed` and comes back the moment the run publishes.
        speed={effectiveSpeed}
        admissibleSpeeds={admissibleSpeeds}
        onSpeed={setSpeed}
        // **Who judges an edited draft** (task 5.12). Supplied only in practice, where the
        // server compiles nothing — see `compilePracticeDraft`. Absent in the tournament path,
        // where the server's `documentValid` is the answer and nothing about it changes.
        onDraftCompile={!showingPractice ? undefined : compilePracticeDraft}
        // The rounds first-run guide (WAG-11): `SandboxScreen`'s own default is `false` so every
        // bare/test mount stays inert (`SandboxScreen.integration.test.tsx`'s `queryByRole('dialog')`
        // assertion among them) — this is the one place that turns it on for a real session, in
        // both the tournament sandbox and practice, and it costs nothing once the store's durable
        // `hasSeenColonyIntro` mirror already says seen.
        firstRunGuideEnabled
      />
    );

  return (
    <>
      {/* No "Opening your sandbox…" curtain any more. There is nothing to wait for at boot: Home
          is the first screen and it is interactive immediately, with the credential restore
          resolving behind it into a Resume affordance (**D-b**). */}
      {bootError !== null && (
        <div data-testid="rounds-boot-error" role="alert" style={NOTICE_STYLE}>
          {bootError}
        </div>
      )}
      {qrJoinNotice !== null && (
        <div data-testid="rounds-qr-join-notice" role="status" style={BANNER_STYLE}>
          {qrJoinNotice}
        </div>
      )}
      {discardedCredential && (
        <div data-testid="rounds-membership-discarded" role="status" style={BANNER_STYLE}>
          Your previous tournament couldn’t be reopened. Join again with a code when you have one.
        </div>
      )}
      {/* **The interrupted-run notice** (`client-practice`). A banner rather than a blocking notice:
          the sandbox underneath is entirely usable and every other run is intact — what the player has
          lost is one run, and what they are owed is an account of where it went rather than an
          interruption of their own.
          Dismissible only in the sense that it does not come back: the run's staged bytes were
          reclaimed as it was raised, so the next reopen has nothing to report. */}
      {interruptedRuns.length > 0 && (
        <div data-testid="practice-run-interrupted" role="status" style={BANNER_STYLE}>
          {interruptedRuns.length === 1
            ? 'Your last run didn’t finish — the page closed while it was still simulating. Everything else is where you left it.'
            : `${interruptedRuns.length} runs didn’t finish — the page closed while they were still simulating. Everything else is where you left it.`}
        </div>
      )}
      {/* **UI/UX audit F-12.** This banner is now only for a restore that could not be *decided*.
          Previously every non-membership restore produced the transport copy, which was false for a
          `5xx` (the hive was reached and failed) and false for a refusal (it answered on purpose).

          A refusal no longer reaches here at all: it clears the credential and shows
          `rounds-membership-discarded` above, whose "join again with a code" wording is now simply
          true. That is only correct because the server stopped answering transient read errors with
          `403` — while it still did, this banner carried a Retry/Forget prompt instead, which had to
          reappear on every boot because nothing could safely decide the credential's fate. Splitting
          `Err` → `500` server-side is what removed that whole state. */}
      {restoreState !== 'none' && (
        <div data-testid="rounds-restore-unavailable" role="status" style={BANNER_STYLE}>
          <span style={{ display: 'block' }}>
            {restoreState === 'server-error'
              ? 'The hive couldn’t check your membership just now. Your membership is still saved — try again in a moment.'
              : 'Couldn’t reach the hive. Your membership is still saved.'}
          </span>
          {/* One action, and it is the player's: a boot that re-asks by itself is what cost two failed
              requests per load. */}
          <button
            type="button"
            data-testid="rounds-restore-retry"
            onClick={() => {
              setRestoreState('none');
              void retryRestore();
            }}
            style={{ ...RESTORE_ACTION_STYLE, marginTop: 8 }}
          >
            Retry
          </button>
        </div>
      )}
      <>
          <AppRoutes
            sound={deps.sound}
            homeScreen={
              <Home
                // Composed at the render site rather than in a memo: the practice entry depends on
                // the practice seams and the session, Home is not memoized, and the other two entry
                // points are one navigation each and close over nothing.
                entry={{
                  practice: practiceEntry,
                  // The leaderboard is Home's other CTA: a navigation and nothing else — the screen
                  // behind it owns its own reads, exactly as Standings does. (A `playVersus` sat
                  // beside it until WAG-108; `/versus` is still routed, but nothing on Home points
                  // at it, so the root hands Home no entry point for it.)
                  openLeaderboard: () => navigateRef.current(ROUNDS_LEADERBOARD_ROUTE),
                  // Reaches MSAL, which the root owns; there is no endpoint of *this* application
                  // behind it, because this application issues no session (design **D20**).
                  signIn: deps.signIn,
                }}
                identity={deps.identity ?? { kind: 'unresolved' }}
                background={
                  <AmbientBackground
                    bytes={ambientLoopBytes()}
                    createRenderer={deps.createRenderer}
                    clock={deps.clock}
                    frameScheduler={deps.frameScheduler}
                  />
                }
              />
            }
            sandboxScreen={sandboxScreenElement}
          />
          {/* **The rounds-owned sub-router**, a sibling of the shared shell rather than a child of it
              (`RoundsRoutes.tsx` carries the whole decision). Each `Routes` matches the location
              independently and renders nothing when no path in it matches, so exactly one of the two ever
              produces a screen — no path is declared in both. Under `GameMode::Live` this component is
              never constructed at all, which is what makes the rounds paths absent there without the
              shared table needing a single conditional about them. */}
          <RoundsRoutes
            standingsScreen={
              <Standings
                source={standingsSource}
                ownJoinOrdinal={boot?.session.joinOrdinal ?? null}
                ownColonyId={phaseSnapshot.colonyId}
                onWatchRound={(round) => {
                  // The round now lives in the address, so entering a review is a navigation and nothing
                  // else — there is no second copy of "which round" to keep in step with it. The viewer
                  // snapshot is dropped unconditionally because the route is about to change: it is keyed
                  // by round and the screen re-reports on mount.
                  setReviewViewer(null);
                  navigateRef.current(roundsReviewPath(round));
                }}
                onBack={() => navigateRef.current(SANDBOX_ROUTE)}
              />
            }
            practiceScreen={sandboxScreenElement}
            leaderboardScreen={
              <Leaderboard
                read={leaderboardRead}
                onBack={() => navigateRef.current(HOME_ROUTE)}
              />
            }
            renderReview={renderGradedReview}
          />
        </>
    </>
  );
}

/** The boot notice: a full-screen message while there is no sandbox to show, in the
 * `forced-refresh-notice` mould (`SessionController`) so a failure is visible rather than a blank
 * screen. */
const NOTICE_STYLE = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  textAlign: 'center',
  background: 'var(--surface-app, #f6efdd)',
  color: 'var(--ink-1, #2b2313)',
  fontFamily: 'var(--font-display)',
  fontSize: 15,
} as const;

/** A non-blocking banner: the sandbox IS usable, the player just needs to know it is a new one. */
const BANNER_STYLE = {
  position: 'fixed',
  top: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 900,
  maxWidth: 'min(520px, calc(100% - 24px))',
  padding: '8px 14px',
  borderRadius: '0 0 12px 12px',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  borderTop: 'none',
  boxShadow: 'var(--shadow-hud)',
  color: 'var(--ink-1)',
  fontSize: 13,
} as const;

/** The restore banner's Retry / Forget controls (UI/UX audit F-12). 44px, so a banner action is as
 * tappable as any other control. */
const RESTORE_ACTION_STYLE = {
  minHeight: 'var(--hit-target)',
  padding: '0 14px',
  borderRadius: 'var(--r-pill)',
  border: '1.5px solid var(--line-strong)',
  background: 'var(--surface-card)',
  color: 'var(--ink-1)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
} as const;
